import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, formatDate, formatDateTime, validatePlate, normalizePlate, validateItalianMobile } from "@/lib/api";
import { uploadWithProgress } from "@/lib/downloadWithProgress";
import { copyToClipboard } from "@/lib/clipboard";
import { autoGenderPatch } from "@/lib/guessGender";
import { useAuth } from "@/context/AuthContext";
import { ClientGdprPanel } from "@/components/ClientGdprPanel";
import { useFirstTimeHint } from "@/lib/useFirstTimeHint";
import { useSyncedPref } from "@/lib/useSyncedPref";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Search, Mail, Phone, Smartphone, Trash2, Pencil, Printer, Car, X, KeyRound, Copy, Check, ShieldOff, Calculator, FileBox, Download, Paperclip, Users, Eye, MapPin, Bell, Clock, Loader2, Send, Sparkles, MessageSquareText, Zap, MoreVertical } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { CopyButton } from "@/components/CopyButton";
import { VehicleAutocomplete, COMMON_BRANDS, COMMON_MODELS_BY_BRAND } from "@/components/VehicleAutocomplete";
import { PlateInput } from "@/components/PlateInput";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { WhatsAppQuickMenu } from "@/components/WhatsAppQuickMenu";
import { SendMessageDialog } from "@/components/SendMessageDialog";
import { ScanButton } from "@/components/ScanButton";
import { CameraButton } from "@/components/CameraButton";
import { DropZone } from "@/components/DropZone";
import { askMultiFileAction } from "@/components/MultiFileUploadDialog";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { AttachmentItem } from "@/components/AttachmentItem";
import { AttachmentLightbox } from "@/components/AttachmentLightbox";
import { AttachmentsHoverPreview } from "@/components/AttachmentsHoverPreview";
import { NotesHoverIcon } from "@/components/NotesHoverIcon";
import { AssignToSelect } from "@/components/AssignToSelect";
import { ClientTimelineDialog } from "@/components/ClientTimelineDialog";
import { ClientPoliciesSection } from "@/components/ClientPoliciesSection";
import { toast } from "sonner";
import { DatePicker } from "@/components/DatePicker";
import { EmailPreviewDialog, shouldSkipEmailPreview } from "@/components/EmailPreviewDialog";
import { downloadWithProgress } from "@/lib/downloadWithProgress";

const empty = {
  // 🆕 Feb 2026 — Privato/Azienda
  subject_type: "private",
  ragione_sociale: "", partita_iva: "", sdi_code: "", pec_email: "",
  legale_rappresentante_name: "", legale_rappresentante_surname: "", legale_rappresentante_fiscal_code: "",
  legale_rappresentante_birth_date: "", legale_rappresentante_birth_place: "", legale_rappresentante_birth_province: "",
  legale_rappresentante_gender: "", legale_rappresentante_role: "", legale_rappresentante_nationality: "IT",
  // 🆕 Feb 2026 — Contatti diretti del Legale Rappresentante (usati per firma OTP azienda)
  legale_rappresentante_email: "", legale_rappresentante_mobile: "",
  name: "", surname: "", email: "", phone: "", mobile: "", fiscal_code: "",
  address: "", house_number: "", city: "", province: "", cap: "", birth_date: "", birth_place: "",
  gender: "M", driving_license_issued_at: "", driving_license_renewed_at: "", driving_license_expires_at: "", driving_license_category: "B", notify_license_expiry: true, notify_id_card_expiry: true, notify_health_card_expiry: true,
  id_card_expires_at: "", health_card_expires_at: "",
  marital_status: "",
  profession: "",
  notes: "", vehicles: [], agent_id: "",
};

const MARITAL_STATUS_OPTIONS = [
  { v: "celibe_nubile", l: "Celibe / Nubile" },
  { v: "coniugato", l: "Coniugato/a" },
  { v: "convivente", l: "Convivente" },
  { v: "separato", l: "Separato/a" },
  { v: "divorziato", l: "Divorziato/a" },
  { v: "vedovo", l: "Vedovo/a" },
  { v: "unito_civilmente", l: "Unito/a civilmente" },
];

const MARITAL_LABEL = Object.fromEntries(MARITAL_STATUS_OPTIONS.map((o) => [o.v, o.l]));

const computeAge = (birthDate) => {
  if (!birthDate) return null;
  try {
    const d = new Date(birthDate);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
    return age >= 0 && age < 150 ? age : null;
  } catch {
    return null;
  }
};

// 🆕 28/07/2026 — Helper condivisi per la scadenza patente
// Format YYYY-MM-DD in fuso LOCALE (evita drift di un giorno con toISOString).
const formatLocalYMD = (d) => {
  if (!(d instanceof Date) || isNaN(d)) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Normalizza categoria patente estraendo il codice prima di spazio/parentesi.
// Es: "B (auto)" → "B", "C1E - camion" → "C1E", "CE" → "CE".
const normalizeLicenseCategory = (raw) => {
  const s = (raw || "B").toUpperCase().trim();
  return s.split(/[\s(]/)[0] || "B";
};

// Ritorna la classe (C, D o A/B) per calcolo scadenza.
// CE segue C, DE segue D, tutte le famiglie C*/D* seguono i rispettivi rami.
const licenseCategoryClass = (rawCat) => {
  const cat = normalizeLicenseCategory(rawCat);
  if (cat.startsWith("C")) return "C"; // C, C1, CE, C1E, ...
  if (cat.startsWith("D")) return "D"; // D, D1, DE, D1E, ...
  return "AB"; // A, AM, A1, A2, B, B1, BE, ...
};

// Calcola scadenza patente secondo la normativa italiana.
// Ritorna { iso, ruleLabel, ageAtRenewal, expected } oppure null se dati insufficienti.
const computeLicenseExpiry = ({ category, birthDate, renewalOrIssueDate }) => {
  if (!birthDate || !renewalOrIssueDate) return null;
  const renewal = new Date(renewalOrIssueDate);
  const birth = new Date(birthDate);
  if (isNaN(renewal) || isNaN(birth)) return null;
  let ageAtRenewal = renewal.getFullYear() - birth.getFullYear();
  const mDiff = renewal.getMonth() - birth.getMonth();
  if (mDiff < 0 || (mDiff === 0 && renewal.getDate() < birth.getDate())) ageAtRenewal -= 1;
  const cls = licenseCategoryClass(category);
  const catNorm = normalizeLicenseCategory(category);
  let yearsToAdd, ruleLabel;
  if (cls === "C") {
    yearsToAdd = ageAtRenewal < 65 ? 5 : 2;
    ruleLabel = `Patente ${catNorm} · ${ageAtRenewal < 65 ? "<65 → 5y" : "65+ → 2y"}`;
  } else if (cls === "D") {
    yearsToAdd = ageAtRenewal < 60 ? 5 : 1;
    ruleLabel = `Patente ${catNorm} · ${ageAtRenewal < 60 ? "<60 → 5y" : "60+ → 1y"}`;
  } else {
    yearsToAdd = ageAtRenewal < 50 ? 10 : ageAtRenewal < 70 ? 5 : ageAtRenewal < 80 ? 3 : 2;
    ruleLabel = `Patente ${catNorm} · ${ageAtRenewal < 50 ? "<50 → 10y" : ageAtRenewal < 70 ? "50-69 → 5y" : ageAtRenewal < 80 ? "70-79 → 3y" : "80+ → 2y"}`;
  }
  // 🆕 28/07/2026 — La patente scade al PRIMO compleanno uguale o successivo alla data
  // (rinnovo + anni di validità). Costruisco la data teorica normativa, poi porto la data
  // sul compleanno nell'anno + yearsToAdd: se cade prima della teorica, sposto all'anno
  // successivo. Il 29/02 negli anni non bisestili è gestito automaticamente da new Date(...).
  const theoretical = new Date(renewal.getFullYear() + yearsToAdd, renewal.getMonth(), renewal.getDate());
  const buildBirthday = (year) => new Date(year, birth.getMonth(), birth.getDate());
  let expected = buildBirthday(renewal.getFullYear() + yearsToAdd);
  if (expected < theoretical) {
    expected = buildBirthday(renewal.getFullYear() + yearsToAdd + 1);
  }
  return { iso: formatLocalYMD(expected), ruleLabel, ageAtRenewal, expected };
};


export default function Clients() {
  const genderManualRef = useRef(new Set());
  const { user: authUser } = useAuth();
  const { canWrite } = usePermissions();
  const navigate = useNavigate();
  useFirstTimeHint("crm_dblclick_hint_v1", "💡 Suggerimento: doppio click sul nome del cliente per aprire la scheda in modifica.");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useSyncedPref("clients_sort_by", "surname_asc");
  // 🆕 Filtri "anomalie" attivabili dalla Dashboard (Admin Health Snapshot)
  const [noCFFilter, setNoCFFilter] = useState(false); // clienti senza codice fiscale
  const [subjectTypeFilter, setSubjectTypeFilter] = useState("all"); // 🆕 Feb 2026 — "all" | "private" | "company"
  const [noPortalFilter, setNoPortalFilter] = useState(false); // clienti senza area riservata attiva
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  // 🆕 28/07/2026 — Ricalcolo automatico della scadenza patente ogni volta che cambiano
  // categoria, data di nascita, data ultimo rinnovo o data rilascio. Aggiorna
  // driving_license_expires_at solo quando il nuovo valore calcolato è diverso da quello
  // attuale (evita loop di setState). Non tocca il campo se mancano dati sorgente.
  useEffect(() => {
    if (form.subject_type === "company") return; // solo Privato
    const baseDate = form.driving_license_renewed_at || form.driving_license_issued_at;
    if (!baseDate || !form.birth_date || !form.driving_license_category) return;
    const res = computeLicenseExpiry({
      category: form.driving_license_category,
      birthDate: form.birth_date,
      renewalOrIssueDate: baseDate,
    });
    if (!res || !res.iso) return;
    if (res.iso === form.driving_license_expires_at) return;
    setForm((f) => ({
      ...f,
      driving_license_expires_at: res.iso,
      // Ricalcolo normativo: azzera audit "aggiornata dal cliente" e "AI extracted"
      driving_license_expires_client_updated_at: null,
      driving_license_ai_extracted_at: null,
    }));
  }, [
    form.driving_license_category,
    form.birth_date,
    form.driving_license_renewed_at,
    form.driving_license_issued_at,
    form.subject_type,
    form.driving_license_expires_at,
  ]);

  // 🆕 Autosave intelligente (30s dopo l'ultimo cambio)
  const [autoSaveAt, setAutoSaveAt] = useState(null);
  const formChangedRef = useRef(false);
  const [loading, setLoading] = useState(false);
  // Duplicati: ricerca live di clienti già esistenti con stesso CF/email/cellulare
  const [duplicates, setDuplicates] = useState([]);
  // Scansione batch di tutti i duplicati esistenti
  const [scanLoading, setScanLoading] = useState(false);
  // Scansione duplicati clienti — chiama backend e mostra risultato come toast
  const runDuplicateScan = async () => {
    setScanLoading(true);
    try {
      const { data } = await api.get("/clients/scan-duplicates");
      const clusters = Array.isArray(data) ? data : (data?.clusters || data?.duplicates || []);
      if (!clusters.length) {
        toast.success("Nessun duplicato trovato 🎉");
      } else {
        const preview = clusters.slice(0, 5).map((c) => {
          const names = (c.clients || c.items || []).map((x) => `${x.surname || ""} ${x.name || ""}`.trim()).filter(Boolean);
          return `• ${c.match_field || c.reason || "match"}: ${names.join(", ")}`;
        }).join("\n");
        toast.warning(`${clusters.length} cluster di possibili duplicati:\n${preview}${clusters.length > 5 ? `\n…e altri ${clusters.length - 5}` : ""}`, { duration: 10000 });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setScanLoading(false);
    }
  };
  const [scanResult, setScanResult] = useState(null); // { total_groups, total_clients, groups: [...] }
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [newVeh, setNewVeh] = useState({ plate: "", brand: "", model: "", year: "" });
  // Predictive suggestions for marca/modello
  const [brandSuggestions, setBrandSuggestions] = useState(COMMON_BRANDS);
  const [modelSuggestions, setModelSuggestions] = useState([]);
  useEffect(() => {
    api.get("/utils/vehicle-brands")
      .then(({ data }) => setBrandSuggestions([...COMMON_BRANDS, ...(data.brands || [])]))
      .catch(() => {});
  }, []);
  useEffect(() => {
    const b = (newVeh.brand || "").trim();
    const localModels = b ? (COMMON_MODELS_BY_BRAND[b.toLowerCase()] || []) : [];
    if (!b) { setModelSuggestions(localModels); return; }
    const t = setTimeout(() => {
      api.get("/utils/vehicle-models", { params: { brand: b } })
        .then(({ data }) => setModelSuggestions([...localModels, ...(data.models || [])]))
        .catch(() => setModelSuggestions(localModels));
    }, 250);
    return () => clearTimeout(t);
  }, [newVeh.brand]);

  // Live duplicate detection: cerca clienti esistenti con stesso CF/email/cellulare mentre l'utente compila
  useEffect(() => {
    if (!open) { setDuplicates([]); return; }
    const fc = (form.fiscal_code || "").trim();
    const em = (form.email || "").trim();
    const mob = (form.mobile || "").trim();
    if (!fc && !em && !mob) { setDuplicates([]); return; }
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const params = {};
        if (fc && fc.replace(/\s/g, "").length === 16) params.fiscal_code = fc;
        if (em && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) params.email = em;
        const mobNorm = mob.replace(/[\s().+-]/g, "").replace(/^(\+39|0039)/, "");
        if (/^3\d{8,9}$/.test(mobNorm)) params.mobile = mobNorm;
        if (editing?.id) params.exclude_id = editing.id;
        if (Object.keys(params).length === 0) { setDuplicates([]); return; }
        const { data } = await api.get("/clients/check-duplicate", { params, signal: ctl.signal });
        setDuplicates(data || []);
      } catch { /* abort or net error */ }
    }, 500);
    return () => { ctl.abort(); clearTimeout(t); };
  }, [open, form.fiscal_code, form.email, form.mobile, editing?.id]);

  // Unisci form corrente con cliente esistente: passa in modalità modifica preservando i dati extra
  const mergeIntoExisting = (clientId) => {
    const existing = items.find((c) => c.id === clientId);
    if (!existing) { toast.error("Cliente non trovato nella lista"); return; }
    // Merge: i dati esistenti hanno priorità ma i campi vuoti vengono completati con quello del form
    const merged = { ...existing };
    Object.keys(form).forEach((k) => {
      const exVal = existing[k];
      const newVal = form[k];
      // Riempi campo solo se esistente vuoto e form ha valore non vuoto
      const isEmpty = (v) => v === undefined || v === null || (typeof v === "string" && !v.trim()) || (Array.isArray(v) && v.length === 0);
      if (isEmpty(exVal) && !isEmpty(newVal)) merged[k] = newVal;
    });
    setEditing(existing);
    setForm(merged);
    setDuplicates([]);
    toast.success(`Unito con ${existing.surname} ${existing.name}. Verifica i dati e salva.`);
  };
  const [portalCreds, setPortalCreds] = useState(null);
  // 🆕 Dialog messaggio cliente
  const [msgTarget, setMsgTarget] = useState(null); // client obj
  const [credsEmailDialog, setCredsEmailDialog] = useState(null); // { to_email, subject, body } | null
  const [credsEmailBusy, setCredsEmailBusy] = useState(false);
  // 🆕 Anteprima email prima dell'invio (unificata via EmailPreviewDialog)
  const [emailPreviewCtx, setEmailPreviewCtx] = useState(null);
  const [credsWaDialog, setCredsWaDialog] = useState(null); // { phone, message } | null
  const [copied, setCopied] = useState(false);
  const [cfLoading, setCfLoading] = useState(false);
  const [docsOpen, setDocsOpen] = useState(null); // client object
  // docKind / docDesc state DEVE essere dichiarato PRIMA dell'useEffect overlay che li referenzia (evita TDZ)
  const [docKind, setDocKind] = useState("altro");
  const [docDesc, setDocDesc] = useState("");
  // Overlay full-screen drag&drop quando il dialog documenti cliente è aperto
  const [dragOverlayActive, setDragOverlayActive] = useState(false);
  useEffect(() => {
    if (!docsOpen?.id) return;
    let counter = 0;
    const isFileDrag = (e) => {
      const types = e.dataTransfer?.types;
      return types && (Array.from(types).includes("Files") || Array.from(types).includes("application/x-moz-file"));
    };
    const onDragEnter = (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      counter += 1;
      if (counter === 1) setDragOverlayActive(true);
    };
    const onDragOver = (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    };
    const onDragLeave = (e) => {
      if (!isFileDrag(e)) return;
      counter = Math.max(0, counter - 1);
      if (counter === 0) setDragOverlayActive(false);
    };
    const onDrop = async (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      counter = 0;
      setDragOverlayActive(false);
      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length === 0) return;
      for (const f of files) {
        try {
          const fd = new FormData();
          fd.append("file", f);
          const desc = encodeURIComponent(docDesc || "");
          await uploadWithProgress({
            label: `📎 ${f.name} → documento cliente`,
            run: (cfg) => api.post(`/clients/${docsOpen.id}/documents?kind=${docKind}&description=${desc}`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
              onUploadProgress: cfg.onUploadProgress,
            }),
          });
        } catch (err) {
          toast.error("Upload " + f.name + " fallito: " + formatApiError(err));
        }
      }
      toast.success(files.length + " document" + (files.length === 1 ? "o caricato" : "i caricati"));
      loadDocs(docsOpen.id);
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
      setDragOverlayActive(false);
    };
  }, [docsOpen?.id, docKind, docDesc]);
  const [contactView, setContactView] = useState(null); // client object per popup contatti
  const [docs, setDocs] = useState([]);
  // Lightbox anteprima allegati a tutto schermo
  const [lightboxIndex, setLightboxIndex] = useState(null);  // null = chiuso, altrimenti indice in `docs`
  // Selezione & invio documenti via email
  const [docsSelectedEmail, setDocsSelectedEmail] = useState({});
  // 🆕 17/07/2026 — Flag documenti (visibile / notifica email) con salvataggio esplicito
  const [docsPendingFlags, setDocsPendingFlags] = useState({});
  const [docsSavingFlags, setDocsSavingFlags] = useState(false);
  const [docsNotifyOpen, setDocsNotifyOpen] = useState(false);
  const [docsNotifyMsg, setDocsNotifyMsg] = useState("");
  const [docsNotifyBusy, setDocsNotifyBusy] = useState(false);
  const [docsSendOpen, setDocsSendOpen] = useState(false);
  const [docsSendForm, setDocsSendForm] = useState({ to_email: "", subject: "", body: "" });
  const [docsSendBusy, setDocsSendBusy] = useState(false);
  const [docsSendLogs, setDocsSendLogs] = useState([]);
  // Cronologia cliente
  const [timelineClient, setTimelineClient] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [clientPolicies, setClientPolicies] = useState([]);
  // 🆕 Feb 2026 — Area privata condivisa: clienti le cui polizze sono visibili
  // nell'area privata del cliente in modifica (uni-direzionale). Reversibile.
  const [portalLinks, setPortalLinks] = useState([]);
  const [portalLinkSearch, setPortalLinkSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, label}
  // OTP cancellazione cliente singolo
  const [deleteOtpStage, setDeleteOtpStage] = useState("confirm"); // "confirm" | "otp"
  const [deleteOtpEmail, setDeleteOtpEmail] = useState("");
  const [deleteOtpCode, setDeleteOtpCode] = useState("");
  const [deleteOtpInfo, setDeleteOtpInfo] = useState(null);
  // OTP bulk delete clienti
  const [bulkOtpStage, setBulkOtpStage] = useState("confirm"); // "confirm" | "otp"
  const [bulkOtpEmail, setBulkOtpEmail] = useState("");
  const [bulkOtpCode, setBulkOtpCode] = useState("");
  const [bulkOtpInfo, setBulkOtpInfo] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false); // step 1/2/0
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [docDeleteTarget, setDocDeleteTarget] = useState(null);
  const [docDeleting, setDocDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/clients");
      setItems(data);
      setSelectedIds(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // 🆕 Feb 2026 — Carica lookup lists dinamiche per Professione e Stato civile.
  // Se la fetch fallisce, si usa la lista statica MARITAL_STATUS_OPTIONS come fallback.
  const [professionLookup, setProfessionLookup] = useState([]);
  const [maritalLookup, setMaritalLookup] = useState([]);
  useEffect(() => {
    api.get("/lookup-lists/profession").then(({ data }) => setProfessionLookup((data || []).filter((x) => x.is_active !== false))).catch(() => {});
    api.get("/lookup-lists/marital_status").then(({ data }) => setMaritalLookup((data || []).filter((x) => x.is_active !== false))).catch(() => {});
  }, []);
  const maritalOptions = maritalLookup.length > 0
    ? maritalLookup.map((m) => ({ v: m.code, l: m.label }))
    : MARITAL_STATUS_OPTIONS;
  const maritalLabelMap = useMemo(() => {
    const base = { ...MARITAL_LABEL };
    maritalLookup.forEach((m) => { base[m.code] = m.label; });
    return base;
  }, [maritalLookup]);
  const professionLabelMap = useMemo(() => {
    const base = {};
    professionLookup.forEach((p) => { base[p.code] = p.label; });
    return base;
  }, [professionLookup]);

  // Highlight + scroll alla riga cliente se l'URL contiene #client-{id} (es. da banner preventivo)
  useEffect(() => {
    if (items.length === 0) return;
    const hash = window.location.hash;
    if (!hash || !hash.startsWith("#client-")) return;
    const id = hash.replace("#client-", "");
    setTimeout(() => {
      const el = document.getElementById(`client-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-blue-400", "bg-blue-100");
        setTimeout(() => el.classList.remove("ring-2", "ring-blue-400", "bg-blue-100"), 2500);
      }
    }, 200);
  }, [items]);

  // Apri direttamente la scheda cliente se l'URL contiene ?focus=<id> (es. dopo "Modifica polizza")
  // Se è presente anche ?return_to=<url> memorizza il return target e ci torna alla chiusura della scheda
  const [returnTo, setReturnTo] = useState(null);
  useEffect(() => {
    if (items.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get("focus");
    if (!focusId) return;
    const target = items.find((c) => c.id === focusId);
    if (!target) return;
    const rt = params.get("return_to");
    if (rt) setReturnTo(rt);
    // Apri la scheda in modifica
    openEdit(target);
    // Pulisci i query param senza ricaricare
    params.delete("focus");
    params.delete("return_to");
    const newSearch = params.toString();
    const newUrl = `${window.location.pathname}${newSearch ? "?" + newSearch : ""}${window.location.hash || ""}`;
    window.history.replaceState({}, "", newUrl);
  }, [items]);

  // 🆕 Click-to-filter dalla Dashboard (Admin Health Snapshot): ?anomaly=no_fc|no_portal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const anom = params.get("anomaly");
    if (!anom) return;
    if (anom === "no_fc") setNoCFFilter(true);
    else if (anom === "no_portal") setNoPortalFilter(true);
    else return;
    params.delete("anomaly");
    const newSearch = params.toString();
    const newUrl = `${window.location.pathname}${newSearch ? "?" + newSearch : ""}${window.location.hash || ""}`;
    window.history.replaceState({}, "", newUrl);
  }, []);

  const filtered = useMemo(() => {
    const filt = items.filter((c) => {
      // 🆕 Filtri anomalie da Dashboard
      if (noCFFilter && (c.fiscal_code || "").trim()) return false;
      if (noPortalFilter && c.portal_enabled) return false;
      // 🆕 Feb 2026 — filtro tipo soggetto (Privato/Azienda)
      if (subjectTypeFilter !== "all") {
        const st = c.subject_type || "private";
        if (st !== subjectTypeFilter) return false;
      }
      if (!q) return true;
      const t = q.toLowerCase();
      return (`${c.surname} ${c.name} ${c.ragione_sociale || ""} ${c.partita_iva || ""} ${c.email || ""} ${c.fiscal_code || ""} ${(c.vehicles || []).map((v) => v.plate).join(" ")}`).toLowerCase().includes(t);
    });
    const it = (v) => (v || "").toString().toLocaleLowerCase("it-IT");
    const ts = (v) => v ? new Date(v).getTime() : 0;
    const comparators = {
      surname_asc:  (a, b) => it(a.surname).localeCompare(it(b.surname), "it") || it(a.name).localeCompare(it(b.name), "it"),
      surname_desc: (a, b) => it(b.surname).localeCompare(it(a.surname), "it") || it(b.name).localeCompare(it(a.name), "it"),
      name_asc:     (a, b) => it(a.name).localeCompare(it(b.name), "it") || it(a.surname).localeCompare(it(b.surname), "it"),
      name_desc:    (a, b) => it(b.name).localeCompare(it(a.name), "it") || it(b.surname).localeCompare(it(a.surname), "it"),
      created_desc: (a, b) => ts(b.created_at) - ts(a.created_at),
      created_asc:  (a, b) => ts(a.created_at) - ts(b.created_at),
      birth_asc:    (a, b) => ts(a.birth_date) - ts(b.birth_date),  // più anziani prima
      birth_desc:   (a, b) => ts(b.birth_date) - ts(a.birth_date),  // più giovani prima
      city_asc:     (a, b) => it(a.city).localeCompare(it(b.city), "it") || it(a.surname).localeCompare(it(b.surname), "it"),
    };
    const cmp = comparators[sortBy] || comparators.surname_asc;
    return [...filt].sort(cmp);
  }, [items, q, sortBy, noCFFilter, noPortalFilter, subjectTypeFilter]);

  const submit = async () => {
    // 🆕 Feb 2026 — Validazione differenziata Privato/Azienda
    if (form.subject_type === "company") {
      if (!(form.ragione_sociale || "").trim()) { toast.error("Ragione Sociale obbligatoria"); return; }
      const piva = (form.partita_iva || "").trim();
      if (!piva || piva.length !== 11 || !/^\d{11}$/.test(piva)) {
        toast.error("Partita IVA obbligatoria: 11 cifre numeriche");
        return;
      }
    }
    // Validazione cellulare se compilato
    if (form.mobile && form.mobile.trim()) {
      const mv = validateItalianMobile(form.mobile);
      if (!mv.ok) { toast.error(`Cellulare: ${mv.message}`); return; }
    }
    // 🆕 Validazione email: obbligatoria salvo flag "Non disponibile"
    if (!form.email_unavailable && !(form.email || "").trim()) {
      toast.error("Email obbligatoria — oppure spunta 'Non disponibile' accanto al campo");
      return;
    }
    setLoading(true);
    try {
      // Forza UPPERCASE su tutti i campi anagrafici di testo (non su email)
      const up = (s) => (s || "").toString().toUpperCase().trim();
      const payload = {
        ...form,
        name: up(form.name),
        surname: up(form.surname),
        birth_place: up(form.birth_place),
        address: up(form.address),
        house_number: up(form.house_number),
        city: up(form.city),
        province: up(form.province),
        cap: (form.cap || "").trim(),
        fiscal_code: up(form.fiscal_code),
        notes: up(form.notes),
        // Campi enum: stringa vuota → null per il backend Literal
        marital_status: form.marital_status || null,
        gender: form.gender || null,
        profession: form.profession || null, // 🆕 Feb 2026 — Professione (code da lookup-list)
        // email rimane case-sensitive (lowercase per convenzione)
        email: (form.email || "").toLowerCase().trim(),
        vehicles: form.vehicles || [],
        agent_id: form.agent_id || null,
      };

      // 🆕 Feb 2026 — Mapping Azienda: ragione_sociale → name, surname="-",
      // per compatibilità con tutte le viste che usano name/surname del cliente
      if (form.subject_type === "company") {
        payload.subject_type = "company";
        payload.ragione_sociale = (form.ragione_sociale || "").toUpperCase().trim();
        payload.name = payload.ragione_sociale;
        payload.surname = "-";
        payload.gender = null;
        payload.birth_date = null;
        payload.birth_place = null;
      } else {
        payload.subject_type = "private";
        // Ripulisce eventuali residui di dati aziendali
        payload.ragione_sociale = null;
        payload.partita_iva = null;
        payload.sdi_code = null;
        payload.pec_email = null;
        payload.legale_rappresentante_name = null;
        payload.legale_rappresentante_surname = null;
        payload.legale_rappresentante_fiscal_code = null;
        payload.legale_rappresentante_birth_date = null;
        payload.legale_rappresentante_birth_place = null;
        payload.legale_rappresentante_birth_province = null;
        payload.legale_rappresentante_gender = null;
        payload.legale_rappresentante_role = null;
        payload.legale_rappresentante_nationality = null;
        // 🆕 Feb 2026 — Azzera anche contatti diretti LR
        payload.legale_rappresentante_email = null;
        payload.legale_rappresentante_mobile = null;
        // 🆕 28/07/2026 — Ricalcola la scadenza patente al salvataggio usando categoria + birth_date + data ultimo rinnovo (o rilascio).
        // In questo modo il valore persistito è sempre coerente con la norma italiana e non resta un valore stantio.
        // Se mancano dati sorgente non si tocca il campo (l'operatore può sempre inserirlo manualmente).
        const baseDate = form.driving_license_renewed_at || form.driving_license_issued_at;
        if (baseDate && form.birth_date && form.driving_license_category) {
          const res = computeLicenseExpiry({
            category: form.driving_license_category,
            birthDate: form.birth_date,
            renewalOrIssueDate: baseDate,
          });
          if (res && res.iso) payload.driving_license_expires_at = res.iso;
        }
      }

      if (editing) await api.put(`/clients/${editing.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(editing ? "Cliente aggiornato" : "Cliente creato");
      closeClientDialog();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const remove = (c) => {
    setDeleteTarget({
      id: c.id,
      label: `${c.surname || ""} ${c.name || ""}`.trim() || c.email || c.id,
    });
    setDeleteOtpStage("confirm");
    setDeleteOtpCode("");
    setDeleteOtpEmail("");
    setDeleteOtpInfo(null);
  };

  const requestDeleteOtp = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { data } = await api.post(`/clients/${deleteTarget.id}/request-delete-otp`);
      setDeleteOtpStage("otp");
      setDeleteOtpEmail(data.email || "");
      setDeleteOtpInfo(data);
      if (data.ok) toast.success(`Codice OTP inviato a ${data.email}`);
      else toast.error("Invio OTP fallito (verifica SMTP)");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const code = (deleteOtpCode || "").trim();
    if (!/^\d{4,10}$/.test(code)) { toast.error("Inserisci il codice OTP"); return; }
    setDeleting(true);
    try {
      await api.delete(`/clients/${deleteTarget.id}`, { headers: { "X-Admin-OTP": code } });
      toast.success("Cliente eliminato");
      setDeleteTarget(null);
      setDeleteOtpCode("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    const filteredIds = filtered.map((c) => c.id);
    const allSelected = filteredIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) filteredIds.forEach((id) => next.delete(id));
      else filteredIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const bulkDelete = () => {
    if (selectedIds.size === 0) return;
    setBulkDeleteOpen(true);
    setBulkOtpStage("confirm");
    setBulkOtpCode("");
    setBulkOtpEmail("");
    setBulkOtpInfo(null);
  };

  const requestBulkDeleteOtp = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/clients/bulk-delete/request-otp", { client_ids: ids });
      setBulkOtpStage("otp");
      setBulkOtpEmail(data.email || "");
      setBulkOtpInfo(data);
      if (data.ok) toast.success(`Codice OTP inviato a ${data.email}`);
      else toast.error("Invio OTP fallito (verifica SMTP)");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkDeleting(false);
    }
  };

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    const code = (bulkOtpCode || "").trim();
    if (ids.length === 0) return;
    if (!/^\d{4,10}$/.test(code)) { toast.error("Inserisci il codice OTP"); return; }
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/clients/bulk-delete/confirm", { client_ids: ids, otp: code });
      toast.success(`${data.deleted_clients} client${data.deleted_clients === 1 ? "e eliminato" : "i eliminati"} · ${data.deleted_policies} polizze cancellate`);
      setBulkDeleteOpen(false);
      setSelectedIds(new Set());
      setBulkOtpCode("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkDeleting(false);
    }
  };

  // Apre una polizza del cliente in /policies?focus=<id>&return_to=/clients?focus=<client_id>
  // Alla chiusura della scheda polizza, l'utente torna sulla scheda di questo cliente.
  const openPolicyFromClient = useCallback((policy) => {
    const clientId = editing?.id;
    if (!clientId || !policy?.id) return;
    const returnUrl = `/clients?focus=${clientId}`;
    // Pulisci eventuale returnTo precedente (es. la scheda era stata aperta da Modifica polizza)
    setReturnTo(null);
    // Chiudi il dialog cliente senza re-navigare
    setOpen(false);
    setForm(empty);
    setEditing(null);
    setDocs([]);
    setDocDesc("");
    setClientPolicies([]);
    setTimeout(() => navigate(`/policies?focus=${policy.id}&return_to=${encodeURIComponent(returnUrl)}`), 50);
  }, [editing, navigate]);

  const openEdit = (c) => {
    setEditing(c);
    api.post(`/clients/${c.id}/access-log`).catch(() => {});
    genderManualRef.current = new Set([c.gender && "gender", c.legale_rappresentante_gender && "legale_rappresentante_gender"].filter(Boolean));
    setForm({ ...empty, ...c, vehicles: c.vehicles || [] });
    setOpen(true);
    formChangedRef.current = false;
    setAutoSaveAt(null);
    // Load documents inline
    loadDocs(c.id);
    // Load policies linked to this client
    api.get("/policies").then(({ data }) => {
      setClientPolicies((data || []).filter((p) => p.client_id === c.id));
    }).catch(() => setClientPolicies([]));
    // 🆕 Load portal links (aree private collegate)
    api.get(`/clients/${c.id}/portal-links`).then(({ data }) => {
      setPortalLinks(data?.linked_clients || []);
    }).catch(() => setPortalLinks([]));
  };

  // 🆕 Autosave: ogni cambio del form schedula un PUT silenzioso dopo 30s di inattività.
  // Attivo solo in modalità modifica (editing.id presente) e dialog aperto.
  useEffect(() => {
    if (!open || !editing?.id) return;
    if (!formChangedRef.current) {
      formChangedRef.current = true;
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const up = (s) => (s || "").toString().toUpperCase().trim();
        const payload = {
          ...form,
          name: up(form.name),
          surname: up(form.surname),
          birth_place: up(form.birth_place),
          address: up(form.address),
          house_number: up(form.house_number),
          city: up(form.city),
          province: up(form.province),
          cap: (form.cap || "").trim(),
          fiscal_code: up(form.fiscal_code),
          notes: up(form.notes),
          marital_status: form.marital_status || null,
          gender: form.gender || null,
          profession: form.profession || null, // 🆕
          email: (form.email || "").toLowerCase().trim(),
          vehicles: form.vehicles || [],
          agent_id: form.agent_id || null,
        };
        await api.put(`/clients/${editing.id}`, payload);
        setAutoSaveAt(new Date());
      } catch { /* silent */ }
    }, 30000);
    return () => clearTimeout(handle);
  }, [form, open, editing?.id]);

  // Chiude la scheda cliente: ripulisce stato e, se è stata aperta con ?return_to=,
  // naviga indietro alla pagina/dialog di provenienza (es. Modifica polizza).
  const closeClientDialog = useCallback(() => {
    setOpen(false);
    genderManualRef.current = new Set();
    setForm(empty);
    setEditing(null);
    setDocs([]);
    setDocDesc("");
    setClientPolicies([]);
    setPortalLinks([]);
    setPortalLinkSearch("");
    if (returnTo) {
      const target = returnTo;
      setReturnTo(null);
      // micro-delay per consentire al Dialog di completare l'unmount
      setTimeout(() => navigate(target), 50);
    }
  }, [navigate, returnTo]);

  const addVehicle = () => {
    if (!newVeh.plate) return;
    // 🆕 Validazione formato targa
    if (!newVeh.plate_override) {
      const _pv = validatePlate(newVeh.plate);
      if (!_pv.ok) {
        toast.error(`Formato targa non valido: ${_pv.message}. Spunta "Formato speciale" per forzare il salvataggio.`);
        return;
      }
    }
    setForm({ ...form, vehicles: [...(form.vehicles || []), { ...newVeh, year: newVeh.year ? parseInt(newVeh.year) : null }] });
    setNewVeh({ plate: "", brand: "", model: "", year: "" });
  };
  const removeVehicle = (i) => setForm({ ...form, vehicles: form.vehicles.filter((_, idx) => idx !== i) });

  // 🆕 Feb 2026 — Aggiunge un cliente all'area privata condivisa (uni-direzionale)
  const addPortalLink = async (linkedClient) => {
    if (!editing?.id || !linkedClient?.id) return;
    if (linkedClient.id === editing.id) {
      toast.error("Non puoi collegare un cliente a sé stesso");
      return;
    }
    if (portalLinks.some((l) => l.id === linkedClient.id)) {
      toast.info("Cliente già collegato");
      return;
    }
    try {
      await api.post(`/clients/${editing.id}/portal-links`, { client_id: linkedClient.id });
      setPortalLinks([...portalLinks, {
        id: linkedClient.id,
        name: linkedClient.name,
        surname: linkedClient.surname,
        email: linkedClient.email,
        mobile: linkedClient.mobile,
        phone: linkedClient.phone,
      }]);
      setPortalLinkSearch("");
      toast.success(`${linkedClient.surname} ${linkedClient.name} collegato all'area privata`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removePortalLink = async (linkedClientId) => {
    if (!editing?.id) return;
    try {
      await api.delete(`/clients/${editing.id}/portal-links/${linkedClientId}`);
      setPortalLinks(portalLinks.filter((l) => l.id !== linkedClientId));
      toast.success("Collegamento rimosso");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const enablePortal = async (c) => {
    if (!window.confirm(`Attivare l'area riservata per ${c.surname} ${c.name}?\nVerrà generata una password monouso.`)) return;
    try {
      const { data } = await api.post(`/clients/${c.id}/enable-portal`);
      setPortalCreds({ ...data, clientName: `${c.surname} ${c.name}`, clientEmail: c.email, clientMobile: c.mobile || c.phone, clientId: c.id, kind: "new" });
      setCopied(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const disablePortal = async (c) => {
    if (!window.confirm("Disattivare l'area riservata? L'utente non potrà più accedere.")) return;
    try {
      await api.post(`/clients/${c.id}/disable-portal`);
      toast.success("Area riservata disattivata");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPortalPassword = async (c) => {
    if (!window.confirm("Generare una nuova password? La vecchia non sarà più valida.")) return;
    try {
      const { data } = await api.post(`/clients/${c.id}/reset-portal-password`);
      setPortalCreds({ ...data, clientName: `${c.surname} ${c.name}`, clientEmail: c.email, clientMobile: c.mobile || c.phone, clientId: c.id, kind: "reset" });
      setCopied(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // 🆕 09/07/2026 — Invia Accesso Rapido (Magic Link) via Email o WhatsApp
  const [magicLinkClient, setMagicLinkClient] = useState(null);
  const [magicLinkSending, setMagicLinkSending] = useState(null);
  const openMagicLinkMenu = (c) => {
    if (!c.portal_enabled) {
      toast.error("L'Area Riservata non è attiva per questo cliente. Attivala prima con il pulsante 🔑.");
      return;
    }
    setMagicLinkClient(c);
  };
  const sendMagicLink = async (channel) => {
    if (!magicLinkClient) return;
    setMagicLinkSending(channel);
    try {
      const { data } = await api.post(`/clients/${magicLinkClient.id}/send-magic-link`, { channel });
      if (channel === "whatsapp") {
        if (data?.wa_url) {
          window.open(data.wa_url, "_blank", "noopener,noreferrer");
          toast.success("WhatsApp aperto con il link precompilato");
        }
      } else {
        toast.success(data?.ok ? `Link inviato a ${data.to}` : "Invio email non riuscito");
      }
      setMagicLinkClient(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMagicLinkSending(null); }
  };

  // One-click: enable o reset del portale, mostra il modal pronto per invio email/WhatsApp
  const sendPortalAccess = async (c) => {
    const isActive = !!c.portal_enabled;
    const confirmMsg = isActive
      ? `Vuoi inviare a ${c.surname} ${c.name} il link dell'area riservata?\n\nVerrà generata una NUOVA password monouso (la vecchia non sarà più valida).`
      : `Vuoi attivare l'area riservata per ${c.surname} ${c.name} e inviare le credenziali?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const endpoint = isActive ? `/clients/${c.id}/reset-portal-password` : `/clients/${c.id}/enable-portal`;
      const { data } = await api.post(endpoint);
      setPortalCreds({ ...data, clientName: `${c.surname} ${c.name}`, clientEmail: c.email, clientMobile: c.mobile || c.phone, clientId: c.id, kind: isActive ? "reset" : "new" });
      setCopied(false);
      if (!isActive) load();
      toast.success("Credenziali pronte. Scegli come inviarle: Email o WhatsApp.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendCredsEmail = () => {
    if (!portalCreds || !portalCreds.clientId) return;
    const intestazione = "Studio Amoroso (intestazione completa nel testo finale)";
    setCredsEmailDialog({
      to_email: portalCreds.clientEmail || portalCreds.email || "",
      subject: `Le tue credenziali di accesso al portale`,
      body: `Gentile ${portalCreds.clientName || ""},\n\nabbiamo attivato per te l'area riservata. Ecco le credenziali di accesso:\n\n  Email: ${portalCreds.email}\n  Password: ${portalCreds.password}\n  Link di accesso: ${window.location.origin}/portale-clienti\n\nTi consigliamo di cambiare la password al primo accesso.\n\nCordiali saluti.`,
      _intestazione: intestazione,
    });
  };

  const confirmSendCredsEmail = async () => {
    if (!credsEmailDialog || !portalCreds?.clientId || credsEmailBusy) return;
    if (!credsEmailDialog.to_email.includes("@")) { toast.error("Email destinatario non valida"); return; }
    const payload = {
      portal_email: portalCreds.email,
      password: portalCreds.password,
      to_email: credsEmailDialog.to_email,
      subject: credsEmailDialog.subject,
      body: credsEmailDialog.body,
    };
    const doActualSend = async () => {
      setCredsEmailBusy(true);
      try {
        const { data } = await api.post(`/clients/${portalCreds.clientId}/send-portal-credentials`, payload);
        toast.success(`Credenziali inviate a ${data.recipient}`);
        setCredsEmailDialog(null);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setCredsEmailBusy(false);
      }
    };
    if (shouldSkipEmailPreview("portal_credentials_client")) {
      await doActualSend();
      return;
    }
    // 🆕 Mostra prima l'anteprima
    setEmailPreviewCtx({
      typeKey: "portal_credentials_client",
      title: "Anteprima invio credenziali portale",
      fetchPreview: async () => {
        const { data } = await api.post(`/clients/${portalCreds.clientId}/send-portal-credentials?dry_run=true`, payload);
        return data;
      },
      onConfirm: doActualSend,
    });
  };

  const openCredsWhatsApp = () => {
    if (!portalCreds) return;
    const phone = portalCreds.clientMobile;
    if (!phone) { toast.warning("Cliente senza numero di cellulare"); return; }
    let normalized = String(phone).replace(/[^\d+]/g, "");
    if (!normalized.startsWith("+")) {
      if (normalized.startsWith("00")) normalized = "+" + normalized.slice(2);
      else if (normalized.startsWith("3")) normalized = "+39" + normalized;
      else normalized = "+" + normalized;
    }
    const portalUrl = `${window.location.origin}/portale-clienti`;
    const message =
      `Ciao ${portalCreds.clientName || ""}, abbiamo attivato la tua area riservata.\n\n` +
      `🔐 Le tue credenziali:\n` +
      `Email: ${portalCreds.email}\n` +
      `Password: ${portalCreds.password}\n\n` +
      `🔗 Accedi qui: ${portalUrl}\n\n` +
      `Ti consigliamo di cambiare la password al primo accesso.`;
    setCredsWaDialog({ phone: normalized.replace("+", ""), displayPhone: normalized, message });
  };

  const confirmWhatsAppSend = () => {
    if (!credsWaDialog) return;
    const msg = encodeURIComponent(credsWaDialog.message);
    window.open(`whatsapp://send?phone=${credsWaDialog.phone}&text=${msg}`, "_blank", "noopener,noreferrer");
    setCredsWaDialog(null);
  };

  const [librettoUploading, setLibrettoUploading] = useState(false);
  const [librettoMatch, setLibrettoMatch] = useState(null);

  const extractFromLibretto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLibrettoUploading(true);
    setLibrettoMatch(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await uploadWithProgress({
        label: `🚗 Analisi libretto: ${file.name}`,
        run: (cfg) => api.post("/utils/extract-libretto", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 90000,
          onUploadProgress: cfg.onUploadProgress,
        }),
      });
      const x = data?.extracted || {};
      const sameName = !form.name || !x.owner_name || form.name.toLowerCase().trim() === (x.owner_name || "").toLowerCase().trim();
      const sameSurname = !form.surname || !x.owner_surname || form.surname.toLowerCase().trim() === (x.owner_surname || "").toLowerCase().trim();
      const sameCF = !form.fiscal_code || !x.owner_fiscal_code || form.fiscal_code.toUpperCase().trim() === (x.owner_fiscal_code || "").toUpperCase().trim();
      const isMatch = sameName && sameSurname && sameCF;
      setLibrettoMatch({
        match: isMatch,
        owner: `${x.owner_name || ""} ${x.owner_surname || ""}`.trim(),
        ownerCF: x.owner_fiscal_code,
        ownershipDate: x.ownership_date,
        previous: x.previous_owners,
      });
      const next = { ...form };
      const fillIfEmpty = (k, v) => { if (v && !next[k]) next[k] = v; };
      fillIfEmpty("name", x.owner_name);
      fillIfEmpty("surname", x.owner_surname);
      fillIfEmpty("fiscal_code", (x.owner_fiscal_code || "").toUpperCase());
      fillIfEmpty("birth_date", x.owner_birth_date);
      fillIfEmpty("birth_place", x.owner_birth_place);
      fillIfEmpty("address", x.owner_address);
      fillIfEmpty("house_number", x.owner_house_number);
      fillIfEmpty("city", x.owner_city);
      fillIfEmpty("province", x.owner_province);
      fillIfEmpty("cap", x.owner_cap);
      const vehicleNew = {
        plate: (x.vehicle_plate || "").toUpperCase(),
        brand: x.vehicle_brand,
        model: x.vehicle_model,
        version: x.vehicle_version,
        year: x.vehicle_year ? parseInt(x.vehicle_year, 10) : null,
        first_registration: x.vehicle_first_registration,
        fuel: x.fuel,
        displacement: x.displacement,
        kw: x.kw,
        cv: x.cv,
        seats: x.seats,
        max_mass: x.max_mass,
        co2_emissions: x.co2_emissions,
        euro_class: x.euro_class,
        vehicle_use: x.vehicle_use,
        vehicle_category: x.vehicle_category,
        chassis_number: x.chassis_number,
        next_review_date: x.next_review_date,
      };
      const idx = (next.vehicles || []).findIndex((v) => v.plate && vehicleNew.plate && v.plate.toUpperCase() === vehicleNew.plate);
      const vehiclesNext = [...(next.vehicles || [])];
      if (idx >= 0) vehiclesNext[idx] = { ...vehiclesNext[idx], ...vehicleNew };
      else if (vehicleNew.plate) vehiclesNext.push(vehicleNew);
      next.vehicles = vehiclesNext;
      setForm(next);
      toast.success(isMatch ? "Libretto importato — intestatario verificato ✓" : "Libretto importato — ATTENZIONE: intestatario diverso");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLibrettoUploading(false); }
  };


  const copyCreds = () => {
    if (!portalCreds) return;
    const text = `Area riservata Assicura\nEmail: ${portalCreds.email}\nPassword: ${portalCreds.password}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const calculateCF = async () => {
    const n = (form.name || "").trim();
    const s = (form.surname || "").trim();
    const bd = (form.birth_date || "").trim();
    const bp = (form.birth_place || "").trim();
    const g = form.gender || "M"; // stesso default mostrato dal Select "Sesso"
    if (!n || !s || !bd || !bp) {
      toast.error("Compila nome, cognome, data e luogo di nascita, sesso");
      return;
    }
    setCfLoading(true);
    try {
      const { data } = await api.post("/utils/fiscal-code", {
        name: n, surname: s, birth_date: bd, birth_place: bp, gender: g,
      });
      setForm((f) => ({ ...f, gender: f.gender || g, fiscal_code: data.fiscal_code }));
      toast.success(`Codice fiscale: ${data.fiscal_code}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCfLoading(false); }
  };

  // 🆕 Feb 2026 — Calcolo CF del Legale Rappresentante
  const calculateLRCF = async () => {
    const n = (form.legale_rappresentante_name || "").trim();
    const s = (form.legale_rappresentante_surname || "").trim();
    const bd = (form.legale_rappresentante_birth_date || "").trim();
    const bp = (form.legale_rappresentante_birth_place || "").trim();
    const g = (form.legale_rappresentante_gender || "").trim();
    if (!n || !s || !bd || !bp || !g) {
      toast.error("LR: compila nome, cognome, data e luogo di nascita, sesso");
      return;
    }
    setCfLoading(true);
    try {
      const { data } = await api.post("/utils/fiscal-code", {
        name: n, surname: s, birth_date: bd, birth_place: bp, gender: g,
      });
      setForm((f) => ({ ...f, legale_rappresentante_fiscal_code: data.fiscal_code }));
      toast.success(`CF Legale Rappresentante: ${data.fiscal_code}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCfLoading(false); }
  };

  // 🆕 Feb 2026 — Decodifica CF Legale Rappresentante → popola data/luogo/sesso/provincia
  const decodeLRCF = async () => {
    const cf = (form.legale_rappresentante_fiscal_code || "").trim().toUpperCase();
    if (cf.length !== 16) {
      toast.error("Inserisci un CF L.R. di 16 caratteri");
      return;
    }
    setCfLoading(true);
    try {
      const { data } = await api.post("/utils/decode-fiscal-code", { fiscal_code: cf });
      if (data.gender) genderManualRef.current.add("legale_rappresentante_gender");
      setForm((f) => ({
        ...f,
        legale_rappresentante_birth_date: data.birth_date || f.legale_rappresentante_birth_date,
        legale_rappresentante_gender: data.gender || f.legale_rappresentante_gender,
        legale_rappresentante_birth_place: (data.birth_place || f.legale_rappresentante_birth_place || "").toUpperCase(),
        legale_rappresentante_birth_province: (data.birth_province || f.legale_rappresentante_birth_province || "").toUpperCase(),
      }));
      const parts = [];
      if (data.birth_date) parts.push(`nato il ${formatDate(data.birth_date)}`);
      if (data.gender) parts.push(`sesso ${data.gender}`);
      if (data.birth_place) parts.push(`a ${data.birth_place}${data.birth_province ? ` (${data.birth_province})` : ""}`);
      toast.success(`LR decodificato: ${parts.join(" · ")}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCfLoading(false); }
  };

  // 🆕 Decodifica il CF e popola i campi anagrafici (data nascita, sesso, luogo, provincia, belfiore)
  const decodeCF = async () => {
    const cf = (form.fiscal_code || "").trim().toUpperCase();
    if (cf.length !== 16) {
      toast.error("Inserisci un Codice Fiscale di 16 caratteri");
      return;
    }
    setCfLoading(true);
    try {
      const { data } = await api.post("/utils/decode-fiscal-code", { fiscal_code: cf });
      if (data.gender) genderManualRef.current.add("gender");
      setForm((f) => ({
        ...f,
        // Mantieni nome/cognome esistenti (non ricavabili dal CF)
        birth_date: data.birth_date || f.birth_date,
        gender: data.gender || f.gender,
        birth_place: (data.birth_place || f.birth_place || "").toUpperCase(),
      }));
      const parts = [];
      if (data.birth_date) parts.push(`nato il ${formatDate(data.birth_date)}`);
      if (data.gender) parts.push(`sesso ${data.gender}`);
      if (data.birth_place) parts.push(`a ${data.birth_place}${data.birth_province ? ` (${data.birth_province})` : ""}`);
      toast.success(`Dati decodificati: ${parts.join(" · ")}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCfLoading(false); }
  };

  const loadDocs = async (cid) => {
    try {
      const { data } = await api.get(`/clients/${cid}/documents`);
      setDocs(data);
      const nextFlags = {};
      (data || []).forEach((a) => {
        nextFlags[a.id] = { client_visible: !!a.client_visible, notify_email_pending: !!a.notify_email_pending };
      });
      setDocsPendingFlags(nextFlags);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const docsFlagsDirty = useMemo(() => (docs || []).some((a) => {
    const p = docsPendingFlags[a.id] || {};
    return (
      (p.client_visible ?? !!a.client_visible) !== !!a.client_visible ||
      (p.notify_email_pending ?? !!a.notify_email_pending) !== !!a.notify_email_pending
    );
  }), [docs, docsPendingFlags]);

  const docsPendingNotifyIds = useMemo(() => (docs || [])
    .filter((a) => (docsPendingFlags[a.id]?.notify_email_pending ?? !!a.notify_email_pending))
    .map((a) => a.id), [docs, docsPendingFlags]);

  const saveDocFlags = async () => {
    if (!docsFlagsDirty || !docsOpen?.id) return;
    setDocsSavingFlags(true);
    try {
      const items = (docs || []).map((a) => ({
        id: a.id,
        client_visible: !!(docsPendingFlags[a.id]?.client_visible),
        notify_email_pending: !!(docsPendingFlags[a.id]?.notify_email_pending),
      }));
      await api.patch("/attachments/batch-flags", { items });
      toast.success("Flag documenti salvati.");
      await loadDocs(docsOpen.id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDocsSavingFlags(false); }
  };

  const sendDocsNotification = async () => {
    if (!docsOpen?.id || docsPendingNotifyIds.length === 0) return;
    setDocsNotifyBusy(true);
    try {
      if (docsFlagsDirty) {
        const items = (docs || []).map((a) => ({
          id: a.id,
          client_visible: !!(docsPendingFlags[a.id]?.client_visible),
          notify_email_pending: !!(docsPendingFlags[a.id]?.notify_email_pending),
        }));
        await api.patch("/attachments/batch-flags", { items });
      }
      const res = await api.post(`/clients/${docsOpen.id}/documents/notify-batch`, {
        attachment_ids: docsPendingNotifyIds,
        custom_message: (docsNotifyMsg || "").trim() || null,
      });
      toast.success(`Notifica inviata a ${res.data.recipient} (${res.data.notified_count} file).`);
      setDocsNotifyOpen(false);
      setDocsNotifyMsg("");
      await loadDocs(docsOpen.id);
      loadDocsSendLogs(docsOpen.id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDocsNotifyBusy(false); }
  };

  const loadDocsSendLogs = async (cid) => {
    try { const { data } = await api.get(`/clients/${cid}/document-send-logs`); setDocsSendLogs(data); }
    catch { setDocsSendLogs([]); }
  };

  const openDocsSendDialog = () => {
    if (!docsOpen) return;
    const ids = Object.entries(docsSelectedEmail).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) { toast.warning("Seleziona almeno un documento"); return; }
    setDocsSendForm({
      to_email: docsOpen.email || "",
      subject: "I tuoi documenti",
      body: `Gentile ${docsOpen.surname || ""} ${docsOpen.name || ""},\n\nin allegato i documenti richiesti.\n\nCordiali saluti.`,
    });
    setDocsSendOpen(true);
  };

  const confirmDocsSend = async () => {
    if (!docsOpen || docsSendBusy) return;
    const ids = Object.entries(docsSelectedEmail).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length || !docsSendForm.to_email.includes("@")) {
      toast.error("Compila i campi obbligatori"); return;
    }
    setDocsSendBusy(true);
    try {
      const { data } = await api.post(`/clients/${docsOpen.id}/send-documents`, {
        attachment_ids: ids,
        to_email: docsSendForm.to_email,
        subject: docsSendForm.subject,
        body: docsSendForm.body,
      });
      toast.success(`Email inviata a ${data.recipient} (${data.sent} file)`);
      setDocsSendOpen(false);
      setDocsSelectedEmail({});
      loadDocsSendLogs(docsOpen.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDocsSendBusy(false);
    }
  };


  const uploadDocFile = async (f) => {
    const cid = docsOpen?.id || editing?.id;
    if (!f || !cid) return;
    setUploadingDoc(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const desc = encodeURIComponent(docDesc || "");
      await api.post(`/clients/${cid}/documents?kind=${docKind}&description=${desc}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento caricato");
      setDocDesc("");
      loadDocs(cid);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingDoc(false); }
  };

  const uploadDoc = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) { e.target.value = ""; return; }
    const processed = await askMultiFileAction(files);
    // Carica i file in sequenza per non saturare la rete; tutti gli errori vengono mostrati uno a uno
    let ok = 0;
    for (const f of processed) {
      try {
        await uploadDocFile(f);
        ok += 1;
      } catch { /* uploadDocFile mostra già il toast */ }
    }
    if (processed.length > 1 && ok > 0) {
      toast.success(`${ok} document${ok === 1 ? "o caricato" : "i caricati"}`);
    }
    e.target.value = "";
  };

  const uploadDocsDrop = async (files) => {
    const processed = await askMultiFileAction(files);
    for (const f of processed) await uploadDocFile(f);
  };

  const downloadDoc = async (att) => {
    // 🆕 Feb 2026 — Download con progress bar (helper globale)
    await downloadWithProgress({
      label: att.original_filename || att.filename || "documento",
      signedUrlPath: `/attachments/${att.id}/signed-url`,
      blobPath: `/attachments/${att.id}/download`,
      filename: att.original_filename || att.filename,
    }).catch(() => { /* toast già mostrato dall'helper */ });
  };

  const removeDoc = (att) => {
    setDocDeleteTarget({ id: att.id, label: att.original_filename || att.id });
  };

  // 🆕 Feb 2026 — Invia documento del cliente per Firma OTP (Centro Firme)
  const requestSignatureForDoc = async (att) => {
    if (!editing?.id) return;
    if (!window.confirm(`Invia "${att.original_filename || att.filename}" al cliente per firma OTP?`)) return;
    try {
      await api.post("/attachments/request-signature", {
        attachment_source: att.policy_id ? "policy" : "quote",  // fallback
        attachment_id: att.id,
        client_id: editing.id,
      });
      // 🆕 Feb 2026 — Il backend invia l'email di invito con pulsante "Firma il documento" (senza OTP).
      toast.success("Invito firma inviato via email");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmRemoveDoc = async () => {
    if (!docDeleteTarget) return;
    setDocDeleting(true);
    try {
      await api.delete(`/attachments/${docDeleteTarget.id}`);
      toast.success("Eliminato");
      setDocDeleteTarget(null);
      if (docsOpen?.id) loadDocs(docsOpen.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDocDeleting(false);
    }
  };

  const sendTestPush = async (c) => {
    try {
      const { data } = await api.post(`/push/test?client_id=${c.id}`);
      if (data?.sent > 0) {
        toast.success(`Notifica inviata a ${data.sent} dispositivo${data.sent === 1 ? "" : "i"}`);
      } else if (data?.skipped === "no_subs") {
        toast.warning(`${c.surname} ${c.name} non ha ancora attivato le notifiche`);
      } else {
        toast.error("Nessun dispositivo raggiungibile");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };


  // Stato per drag&drop + progress estrazione
  const [extracting, setExtracting] = useState(false);
  const [extractKind, setExtractKind] = useState(null);  // "carta_identita" | "patente"
  const [extractStage, setExtractStage] = useState("");  // "uploading" | "ocr" | "ai" | "done"
  const [uploadProgress, setUploadProgress] = useState(0);

  const importFromDocument = async (file, kind = "carta_identita") => {
    if (!file) return;
    if (extracting) return;
    setExtracting(true);
    setExtractKind(kind);
    setExtractStage("uploading");
    setUploadProgress(0);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/utils/extract-pdf?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setUploadProgress(pct);
            if (pct >= 100) setExtractStage("ai");
          }
        },
      });
      const ex = data?.extracted || {};
      setForm((prev) => ({
        ...prev,
        name: ex.name || prev.name,
        surname: ex.surname || prev.surname,
        fiscal_code: (ex.fiscal_code || prev.fiscal_code || "").toUpperCase(),
        birth_date: ex.birth_date || prev.birth_date,
        birth_place: ex.birth_place || prev.birth_place,
        gender: ex.gender || prev.gender,
        address: ex.address || prev.address,
        house_number: ex.house_number || prev.house_number,
        city: ex.city || prev.city,
        cap: ex.cap || prev.cap,
        province: ex.province || prev.province,
        driving_license_issued_at: ex.driving_license_issued_at || prev.driving_license_issued_at,
      }));
      const filled = Object.values(ex).filter((v) => v !== null && v !== undefined && v !== "").length;
      setExtractStage("done");
      if (filled > 0) toast.success(`Estratti ${filled} campi dal documento.`);
      else toast.warning("Nessun dato leggibile. Riprova con una scansione più nitida.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExtracting(false);
      setTimeout(() => { setExtractStage(""); setUploadProgress(0); setExtractKind(null); }, 1200);
    }
  };

  // Wrapper retrocompatibile per input file legacy
  const importFromPDF = async (e, kind = "carta_identita") => {
    const f = e.target.files?.[0];
    if (f) await importFromDocument(f, kind);
    e.target.value = "";
  };



  return (
    <div className="space-y-6 max-w-7xl mx-auto animate-fade-up" data-testid="clients-page">
      {/* Overlay full-screen drag&drop documenti cliente */}
      {dragOverlayActive && (
        <div
          data-testid="client-drag-overlay"
          className="fixed inset-0 z-[60] pointer-events-none flex items-center justify-center bg-blue-600/30 backdrop-blur-sm border-4 border-dashed border-blue-500 animate-fade-in"
        >
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 text-center max-w-md mx-4">
            <div className="text-5xl mb-2">📎</div>
            <p className="text-lg font-semibold text-slate-900">
              Rilascia per caricare i documenti del cliente
            </p>
            <p className="text-xs text-slate-500 mt-1.5">
              Tipologia attiva: <strong>{docKind.replace(/_/g, " ")}</strong> · PDF/immagini, max 10 MB
            </p>
          </div>
        </div>
      )}
      <header className="flex items-center justify-between flex-wrap gap-4 print:hidden">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Anagrafica</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3"><Users className="h-8 w-8 sm:h-10 sm:w-10 text-blue-600 bg-blue-100 rounded-lg p-1.5" />Clienti</h1>
          <p className="text-slate-500 mt-1">{items.length} totali</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" data-testid="scan-duplicates-btn" onClick={() => runDuplicateScan()} disabled={scanLoading} className="text-xs sm:text-sm">
            {scanLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Search className="h-4 w-4 mr-1.5" />}
            <span className="hidden sm:inline">Scansiona </span>duplicati
          </Button>
          <Button variant="outline" data-testid="print-clients" onClick={() => window.print()} className="text-xs sm:text-sm"><Printer className="h-4 w-4 mr-1.5" /> Stampa</Button>
          <Dialog open={open} onOpenChange={(o) => { if (!o) { closeClientDialog(); } else { setOpen(true); } }}>
            <DialogTrigger asChild>
              {canWrite("clients") ? (
                <Button data-testid="add-client-btn" className="bg-[#0F172A] hover:bg-[#1E293B] text-xs sm:text-sm">
                  <Plus className="h-4 w-4 mr-1.5" /> <span className="hidden sm:inline">Nuovo </span>cliente
                </Button>
              ) : <span />}
            </DialogTrigger>
            <DialogContent className="w-[92vw] max-w-6xl max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle></DialogHeader>

              {/* Banner duplicati: appare se CF/email/cellulare coincide con altri clienti già censiti */}
              {duplicates.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-2.5" data-testid="duplicate-warning">
                  <span className="text-amber-600 text-lg leading-none mt-0.5">⚠</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-amber-900">
                      Possibile duplicato: {duplicates.length === 1 ? "1 cliente già esistente" : `${duplicates.length} clienti già esistenti`} con dati simili
                    </div>
                    <ul className="mt-1.5 space-y-1">
                      {duplicates.map((d) => (
                        <li key={d.id} className="text-xs text-amber-900 flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{d.surname} {d.name}</span>
                          <span className="text-amber-700">·</span>
                          <span className="text-amber-700">{d.email || d.mobile || d.fiscal_code}</span>
                          {Array.isArray(d.match_on) && d.match_on.length > 0 && (
                            <span className="inline-flex gap-1">
                              {d.match_on.map((m) => (
                                <span key={m} className="px-1.5 py-0.5 text-[10px] uppercase font-bold rounded bg-amber-200 text-amber-800">{m}</span>
                              ))}
                            </span>
                          )}
                          {d.created_at && <span className="text-amber-600 text-[10px]">creato {formatDate(d.created_at)}</span>}
                          <button
                            type="button"
                            data-testid={`merge-with-${d.id}`}
                            onClick={() => mergeIntoExisting(d.id)}
                            className="ml-auto text-[11px] font-semibold text-blue-700 hover:text-blue-900 hover:underline inline-flex items-center gap-1 bg-white border border-blue-200 rounded px-2 py-0.5"
                          >
                            🔗 Unisci con esistente
                          </button>
                        </li>
                      ))}
                    </ul>
                    <p className="text-[10px] text-amber-700 mt-1.5">
                      Verifica prima di salvare: potrebbe essere lo stesso cliente. Puoi unirlo a uno esistente o salvarlo come nuovo se hai motivo di farlo.
                    </p>
                  </div>
                </div>
              )}

              {/* Drag&drop area per import documenti (CI / Patente / Libretto) */}
              <DropZone
                testid="client-import-drop"
                accept={["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"]}
                multiple={false}
                disabled={extracting || librettoUploading}
                onFiles={(files) => {
                  // Determina automaticamente il tipo dal kind selezionato (default CI)
                  if (files?.[0]) importFromDocument(files[0], extractKind || "carta_identita");
                }}
                className={`rounded-md border-2 border-dashed mb-3 ${extracting ? "border-blue-400 bg-blue-50/40" : "border-blue-200 bg-blue-50/40 hover:border-blue-300"}`}
              >
                <div className="p-3">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-blue-700 uppercase tracking-wide">⚡ Importa da documento:</span>
                    <label className="cursor-pointer">
                      <input data-testid="import-ci-pdf" type="file" accept="application/pdf,image/*" className="hidden" disabled={extracting} onChange={(e) => { setExtractKind("carta_identita"); importFromPDF(e, "carta_identita"); }} />
                      <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border transition-colors ${extracting && extractKind === "carta_identita" ? "border-blue-500 bg-blue-100 text-blue-800" : "border-blue-300 hover:bg-blue-100 text-blue-700"}`}>📄 Carta d&apos;identità</span>
                    </label>
                    <label className="cursor-pointer">
                      <input data-testid="import-patente-pdf" type="file" accept="application/pdf,image/*" className="hidden" disabled={extracting} onChange={(e) => { setExtractKind("patente"); importFromPDF(e, "patente"); }} />
                      <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border transition-colors ${extracting && extractKind === "patente" ? "border-blue-500 bg-blue-100 text-blue-800" : "border-blue-300 hover:bg-blue-100 text-blue-700"}`}>🚗 Patente</span>
                    </label>
                    <label className="cursor-pointer">
                      <input data-testid="import-libretto" type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={extractFromLibretto} disabled={librettoUploading} />
                      <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-emerald-300 hover:bg-emerald-100 text-emerald-700">
                        {librettoUploading ? "⏳ Analisi…" : "📕 Libretto (PDF/foto)"}
                      </span>
                    </label>
                    <span className="text-slate-300">|</span>
                    <ScanButton
                      testid="scan-ci"
                      label="Importa CI da scanner"
                      variant="outline"
                      disabled={extracting}
                      onFile={(f) => { setExtractKind("carta_identita"); importFromDocument(f, "carta_identita"); }}
                    />
<CameraButton testid="cam-scan-ci" label="Foto CI da fotocamera" variant="outline" disabled={extracting} onFile={(f) => { setExtractKind("carta_identita"); importFromDocument(f, "carta_identita"); }} />
                    <ScanButton
                      testid="scan-patente"
                      label="Importa Patente da scanner"
                      variant="outline"
                      disabled={extracting}
                      onFile={(f) => { setExtractKind("patente"); importFromDocument(f, "patente"); }}
                    />
<CameraButton testid="cam-scan-patente" label="Foto Patente da fotocamera" variant="outline" disabled={extracting} onFile={(f) => { setExtractKind("patente"); importFromDocument(f, "patente"); }} />
                    <ScanButton
                      testid="scan-libretto"
                      label="Importa Libretto da scanner"
                      variant="outline"
                      disabled={librettoUploading}
                      onFile={async (f) => {
                        const fakeEvent = { target: { files: [f], value: "" } };
                        await extractFromLibretto(fakeEvent);
                      }}
                    />
                    <CameraButton
                      testid="cam-scan-libretto"
                      label="📷 Foto Libretto"
                      variant="outline"
                      disabled={librettoUploading}
                      onFile={async (f) => {
                        const fakeEvent = { target: { files: [f], value: "" } };
                        await extractFromLibretto(fakeEvent);
                      }}
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    Trascina qui un file <strong>PDF, JPG, PNG o HEIC</strong> oppure clicca sui pulsanti.
                    L&apos;AI legge anche scansioni e foto (max 10 MB) e compila automaticamente i campi.
                  </p>
                  {extracting && (
                    <div data-testid="client-extract-progress" className="mt-2.5">
                      <div className="flex items-center justify-between text-[11px] text-blue-700 mb-1">
                        <span className="font-semibold">
                          {extractStage === "uploading" && `📤 Caricamento ${extractKind === "patente" ? "patente" : "carta d'identità"}…`}
                          {extractStage === "ai" && "🧠 L'AI sta analizzando il documento…"}
                          {extractStage === "done" && "✅ Dati estratti"}
                        </span>
                        <span className="tabular-nums">{extractStage === "uploading" ? `${uploadProgress}%` : (extractStage === "ai" ? "elaborazione…" : "100%")}</span>
                      </div>
                      <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
                        {extractStage === "uploading" ? (
                          <div className="h-full bg-blue-500 transition-all" style={{ width: `${uploadProgress}%` }} />
                        ) : extractStage === "ai" ? (
                          <div className="h-full bg-gradient-to-r from-blue-400 via-blue-600 to-blue-400 bg-[length:200%_100%] animate-pulse" style={{ width: "100%", animation: "shimmer 1.5s linear infinite" }} />
                        ) : (
                          <div className="h-full bg-emerald-500 w-full" />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </DropZone>

              {librettoMatch && (
                <div
                  data-testid="libretto-match-banner"
                  className={`rounded-md p-3 mb-3 text-sm ${
                    librettoMatch.match
                      ? "bg-emerald-50 border border-emerald-200 text-emerald-900"
                      : "bg-amber-50 border border-amber-300 text-amber-900"
                  }`}
                >
                  <p className="font-semibold mb-1">
                    {librettoMatch.match
                      ? "✅ Intestatario verificato"
                      : "⚠️ ATTENZIONE: l'intestatario sul libretto non corrisponde al cliente"}
                  </p>
                  <p className="text-xs">
                    Ultimo intestatario rilevato: <strong>{librettoMatch.owner || "—"}</strong>
                    {librettoMatch.ownerCF && <> · CF <span className="font-mono">{librettoMatch.ownerCF}</span></>}
                    {librettoMatch.ownershipDate && <> · dal {formatDate(librettoMatch.ownershipDate)}</>}
                  </p>
                  {librettoMatch.previous && Array.isArray(librettoMatch.previous) && librettoMatch.previous.length > 0 && (
                    <p className="text-[11px] mt-1 opacity-80">Precedenti intestatari: {librettoMatch.previous.map((x) => typeof x === "string" ? x : (x.name || JSON.stringify(x))).join(" · ")}</p>
                  )}
                </div>
              )}
              <div className="space-y-3">
                {/* 🆕 Assegna a — spostato in cima al primo posto */}
                <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3" data-testid="client-assign-card">
                  <AssignToSelect
                    value={form.agent_id}
                    onChange={(v) => setForm({ ...form, agent_id: v })}
                    testId="client-assign-to"
                  />
                </div>

                {/* 🆕 Feb 2026 — Selettore Tipo Soggetto */}
                <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3" data-testid="client-subject-type-card">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-blue-700 mb-2">Tipo Soggetto</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      data-testid="client-subject-type-private"
                      onClick={() => setForm({ ...form, subject_type: "private" })}
                      className={`border-2 rounded-md p-2 text-left transition-all ${(form.subject_type || "private") === "private" ? "border-blue-600 bg-white ring-2 ring-blue-200" : "border-slate-200 bg-white hover:border-blue-400"}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">👤</span>
                        <div>
                          <p className="font-semibold text-xs">Privato</p>
                          <p className="text-[10px] text-slate-500">Persona fisica</p>
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      data-testid="client-subject-type-company"
                      onClick={() => setForm({ ...form, subject_type: "company" })}
                      className={`border-2 rounded-md p-2 text-left transition-all ${form.subject_type === "company" ? "border-blue-600 bg-white ring-2 ring-blue-200" : "border-slate-200 bg-white hover:border-blue-400"}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🏢</span>
                        <div>
                          <p className="font-semibold text-xs">Azienda</p>
                          <p className="text-[10px] text-slate-500">Persona giuridica</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* 🆕 Feb 2026 — Sezione Dati Azienda (visibile solo se subject_type=company) */}
                {form.subject_type === "company" && (
                  <fieldset className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3">
                    <legend className="px-2 text-[11px] font-semibold uppercase tracking-wider text-indigo-700 bg-white rounded">🏢 Dati Azienda</legend>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2">
                        <Label>Ragione Sociale <span className="text-red-500">*</span></Label>
                        <Input
                          data-testid="client-ragione-sociale"
                          className="uppercase"
                          placeholder="es. ROSSI COSTRUZIONI SRL"
                          value={form.ragione_sociale || ""}
                          onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value.toUpperCase() })}
                        />
                      </div>
                      <div>
                        <Label>Partita IVA <span className="text-red-500">*</span></Label>
                        {(() => {
                          const piva = (form.partita_iva || "").trim();
                          const validLen = piva.length === 11 && /^\d{11}$/.test(piva);
                          let checksumOk = false;
                          if (validLen) {
                            let s = 0;
                            for (let i = 0; i < 11; i++) {
                              let n = parseInt(piva[i], 10);
                              if (i % 2 === 1) { n *= 2; if (n > 9) n -= 9; }
                              s += n;
                            }
                            checksumOk = (s % 10) === 0;
                          }
                          const invalid = piva.length > 0 && (!validLen || !checksumOk);
                          const valid = piva.length === 11 && checksumOk;
                          return (
                            <>
                              <Input
                                data-testid="client-partita-iva"
                                className={`font-mono ${invalid ? "border-rose-500 bg-rose-50/30" : valid ? "border-emerald-500 bg-emerald-50/30" : ""}`}
                                placeholder="11 cifre"
                                value={piva}
                                onChange={(e) => setForm({ ...form, partita_iva: (e.target.value || "").replace(/\D/g, "").slice(0, 11) })}
                                maxLength={11}
                                inputMode="numeric"
                              />
                              {invalid && <p className="text-[10px] text-rose-600 mt-0.5">P.IVA non valida (11 cifre, checksum errato)</p>}
                              {valid && <p className="text-[10px] text-emerald-700 mt-0.5">✓ Formalmente valida</p>}
                            </>
                          );
                        })()}
                      </div>
                      <div>
                        <Label>Codice Univoco SDI</Label>
                        <Input
                          data-testid="client-sdi-code"
                          className="font-mono uppercase"
                          placeholder="7 caratteri"
                          maxLength={7}
                          value={form.sdi_code || ""}
                          onChange={(e) => setForm({ ...form, sdi_code: (e.target.value || "").toUpperCase().slice(0, 7) })}
                        />
                      </div>
                      <div>
                        <Label>Codice Fiscale Societario</Label>
                        <Input
                          data-testid="client-cf-societario"
                          className="font-mono uppercase"
                          placeholder="Se diverso dalla P.IVA"
                          maxLength={16}
                          value={form.fiscal_code || ""}
                          onChange={(e) => setForm({ ...form, fiscal_code: (e.target.value || "").toUpperCase().trim() })}
                        />
                        <p className="text-[10px] text-slate-500 mt-0.5">Compila solo se diverso dalla P.IVA</p>
                      </div>
                      <div className="sm:col-span-2">
                        <Label>PEC Aziendale</Label>
                        <Input
                          data-testid="client-pec-email"
                          type="email"
                          placeholder="pec@aziendarossi.it"
                          value={form.pec_email || ""}
                          onChange={(e) => setForm({ ...form, pec_email: e.target.value })}
                        />
                      </div>
                      {/* Legale Rappresentante */}
                      <div className="sm:col-span-2 pt-2 border-t border-indigo-200 mt-1">
                        <p className="text-[11px] uppercase tracking-wide text-indigo-700 font-bold mb-2">👔 Legale Rappresentante</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <Label>Cognome L.R.</Label>
                            <Input
                              data-testid="client-lr-surname"
                              className="uppercase"
                              value={form.legale_rappresentante_surname || ""}
                              onChange={(e) => setForm({ ...form, legale_rappresentante_surname: e.target.value.toUpperCase() })}
                            />
                          </div>
                          <div>
                            <Label>Nome L.R.</Label>
                            <Input
                              data-testid="client-lr-name"
                              className="uppercase"
                              value={form.legale_rappresentante_name || ""}
                              onChange={(e) => { const v = e.target.value.toUpperCase(); setForm({ ...form, legale_rappresentante_name: v, ...autoGenderPatch(v, "legale_rappresentante_gender", genderManualRef.current) }); }}
                            />
                          </div>
                          <div>
                            <Label>Codice Fiscale L.R.</Label>
                            <div className="flex gap-1.5">
                              <Input
                                data-testid="client-lr-cf"
                                className="font-mono uppercase flex-1"
                                maxLength={16}
                                value={form.legale_rappresentante_fiscal_code || ""}
                                onChange={(e) => setForm({ ...form, legale_rappresentante_fiscal_code: (e.target.value || "").toUpperCase().trim() })}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={decodeLRCF}
                                disabled={cfLoading}
                                data-testid="client-lr-cf-decode"
                                title="Decodifica CF L.R. e popola data/luogo/sesso/provincia"
                                className="border-purple-300 text-purple-700 hover:bg-purple-50 shrink-0"
                              >
                                <Sparkles className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={calculateLRCF}
                                disabled={cfLoading}
                                data-testid="client-lr-cf-calc"
                                title="Calcola CF dai dati anagrafici del Legale Rappresentante"
                                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 shrink-0"
                              >
                                <Calculator className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <div>
                            <Label>Data di Nascita L.R.</Label>
                            <DatePicker
                              data-testid="client-lr-birth-date"
                              value={form.legale_rappresentante_birth_date || ""}
                              onChange={(e) => setForm({ ...form, legale_rappresentante_birth_date: e.target.value })}
                            />
                          </div>
                          <div>
                            <Label>Luogo di Nascita L.R.</Label>
                            <Input
                              data-testid="client-lr-birth-place"
                              className="uppercase"
                              placeholder="Es. ROMA"
                              value={form.legale_rappresentante_birth_place || ""}
                              onChange={(e) => setForm({ ...form, legale_rappresentante_birth_place: e.target.value.toUpperCase() })}
                            />
                          </div>
                          <div>
                            <Label>Provincia Nascita L.R.</Label>
                            <Input
                              data-testid="client-lr-birth-province"
                              className="uppercase"
                              maxLength={2}
                              placeholder="Es. RM"
                              value={form.legale_rappresentante_birth_province || ""}
                              onChange={(e) => setForm({ ...form, legale_rappresentante_birth_province: e.target.value.toUpperCase().slice(0, 2) })}
                            />
                          </div>
                          <div>
                            <Label>Sesso L.R.</Label>
                            <Select value={form.legale_rappresentante_gender || ""} onValueChange={(v) => { genderManualRef.current.add("legale_rappresentante_gender"); setForm({ ...form, legale_rappresentante_gender: v }); }}>
                              <SelectTrigger data-testid="client-lr-gender"><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="M">Maschio</SelectItem>
                                <SelectItem value="F">Femmina</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Carica</Label>
                            <Select value={form.legale_rappresentante_role || ""} onValueChange={(v) => setForm({ ...form, legale_rappresentante_role: v })}>
                              <SelectTrigger data-testid="client-lr-role"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Amministratore Unico">Amministratore Unico</SelectItem>
                                <SelectItem value="Presidente CDA">Presidente CDA</SelectItem>
                                <SelectItem value="Amministratore Delegato">Amministratore Delegato</SelectItem>
                                <SelectItem value="Consigliere Delegato">Consigliere Delegato</SelectItem>
                                <SelectItem value="Legale Rappresentante">Legale Rappresentante</SelectItem>
                                <SelectItem value="Titolare">Titolare (ditta individuale)</SelectItem>
                                <SelectItem value="Socio Accomandatario">Socio Accomandatario</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Nazionalità</Label>
                            <Input
                              data-testid="client-lr-nationality"
                              className="uppercase"
                              maxLength={2}
                              placeholder="IT"
                              value={form.legale_rappresentante_nationality || ""}
                              onChange={(e) => setForm({ ...form, legale_rappresentante_nationality: e.target.value.toUpperCase().slice(0, 2) })}
                            />
                          </div>
                          {/* 🆕 Feb 2026 — Contatti diretti Legale Rappresentante (usati per firma OTP azienda) */}
                          <div>
                            <Label>📱 Cellulare L.R. <span className="text-[10px] text-purple-700 font-medium">(firma OTP)</span></Label>
                            <Input
                              data-testid="client-lr-mobile"
                              type="tel"
                              placeholder="+39 333 1234567"
                              value={form.legale_rappresentante_mobile || ""}
                              onChange={(e) => setForm({ ...form, legale_rappresentante_mobile: e.target.value })}
                            />
                            <p className="text-[10px] text-slate-500 mt-1">Se compilato, l&apos;OTP di firma SMS verrà inviato a questo numero invece che a quello aziendale.</p>
                          </div>
                          <div>
                            <Label>📧 Email L.R.</Label>
                            <Input
                              data-testid="client-lr-email"
                              type="email"
                              placeholder="mario.rossi@azienda.it"
                              value={form.legale_rappresentante_email || ""}
                              onChange={(e) => setForm({ ...form, legale_rappresentante_email: e.target.value })}
                            />
                            <p className="text-[10px] text-slate-500 mt-1">Se compilata, l&apos;OTP di firma via email arriva direttamente al Legale Rappresentante.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-2 italic">
                      Per gli indirizzi (Sede Legale) e i contatti (Email/Telefono), usa le sezioni sottostanti.
                    </p>
                  </fieldset>
                )}

                {/* Sezione Anagrafica (azzurro tenue) — Solo per Privati */}
                {form.subject_type !== "company" && (
                <fieldset className="rounded-lg border border-sky-200 bg-sky-50/40 p-3">
                  <legend className="px-2 text-[11px] font-semibold uppercase tracking-wider text-sky-700 bg-white rounded">
                    🪪 Anagrafica
                  </legend>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Cognome <span className="text-red-500">*</span></Label>
                  <div className="flex gap-1.5">
                    <Input data-testid="client-surname" className="uppercase flex-1" placeholder="Dato obbligatorio" value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value.toUpperCase() })} />
                    <CopyButton testid="client-surname-copy" label="Cognome" value={form.surname} />
                  </div>
                </div>
                <div><Label>Nome <span className="text-red-500">*</span></Label>
                  <div className="flex gap-1.5">
                    <Input data-testid="client-name" className="uppercase flex-1" placeholder="Dato obbligatorio" value={form.name} onChange={(e) => { const v = e.target.value.toUpperCase(); setForm({ ...form, name: v, ...autoGenderPatch(v, "gender", genderManualRef.current) }); }} />
                    <CopyButton testid="client-name-copy" label="Nome" value={form.name} />
                  </div>
                </div>
                <div>
                  <Label>Sesso</Label>
                  <Select value={form.gender || "M"} onValueChange={(v) => { genderManualRef.current.add("gender"); setForm({ ...form, gender: v }); }}>
                    <SelectTrigger data-testid="client-gender"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Maschio</SelectItem>
                      <SelectItem value="F">Femmina</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Data di nascita {form.birth_date && computeAge(form.birth_date) !== null && (
                    <span className="text-xs font-semibold text-blue-700 ml-1.5" data-testid="client-age">({computeAge(form.birth_date)} anni)</span>
                  )}</Label>
                  <DatePicker data-testid="client-birthdate" value={form.birth_date || ""} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label>Luogo di nascita</Label>
                  <CityAutocomplete
                    testid="client-birthplace"
                    value={form.birth_place}
                    onChange={(v) => setForm({ ...form, birth_place: (v || "").toUpperCase() })}
                    inputClassName="uppercase"
                  />
                </div>
                <div className="col-span-2 flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="flex items-center gap-2">Codice fiscale
                      <span className="text-xs text-slate-400 normal-case">(auto-calcolato)</span>
                    </Label>
                    <Input data-testid="client-fc" value={form.fiscal_code || ""} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })} className="font-mono uppercase" />
                  </div>
                  <CopyButton testid="client-fc-copy" label="Codice Fiscale" value={form.fiscal_code} size="md" />
                  <Button
                    type="button"
                    data-testid="decode-cf-btn"
                    onClick={decodeCF}
                    disabled={cfLoading}
                    variant="outline"
                    className="border-violet-200 text-violet-700 hover:bg-violet-50"
                    title="Decodifica il CF e popola data/luogo di nascita e sesso"
                  >
                    <Sparkles className="h-4 w-4 mr-1.5" /> {cfLoading ? "…" : "Decodifica"}
                  </Button>
                  <Button
                    type="button"
                    data-testid="calc-cf-btn"
                    onClick={calculateCF}
                    disabled={cfLoading}
                    variant="outline"
                    className="border-blue-200 text-blue-700 hover:bg-blue-50"
                  >
                    <Calculator className="h-4 w-4 mr-1.5" /> {cfLoading ? "Calcolo…" : "Calcola"}
                  </Button>
                </div>
                {/* 🆕 Feb 2026 — Stato civile + Professione accorpati dentro Anagrafica */}
                <div>
                  <Label>💍 Stato civile</Label>
                  <Select value={form.marital_status || "__none__"} onValueChange={(v) => setForm({ ...form, marital_status: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="client-marital-status"><SelectValue placeholder="— Non specificato —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Non specificato —</SelectItem>
                      {maritalOptions.map((o) => (
                        <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500 mt-0.5">Voci gestibili in <em>Liste preventivo → Stato civile</em>.</p>
                </div>
                <div>
                  <Label>💼 Professione</Label>
                  <Select value={form.profession || "__none__"} onValueChange={(v) => setForm({ ...form, profession: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="client-profession"><SelectValue placeholder="— Non specificata —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Non specificata —</SelectItem>
                      {professionLookup.map((p) => (
                        <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500 mt-0.5">Voci gestibili in <em>Liste preventivo → Professione</em>.</p>
                </div>
                  </div>
                </fieldset>
                )}

                {/* Sezione Contatti (verde tenue) */}
                <fieldset className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
                  <legend className="px-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 bg-white rounded">
                    📞 Contatti
                  </legend>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label>Email {!form.email_unavailable && <span className="text-rose-500">*</span>}</Label>
                    <label className="inline-flex items-center gap-1.5 text-[10px] text-slate-600 cursor-pointer select-none" title="Spunta se il cliente non ha un indirizzo email">
                      <input
                        type="checkbox"
                        data-testid="client-email-unavailable"
                        checked={!!form.email_unavailable}
                        onChange={(e) => setForm({ ...form, email_unavailable: e.target.checked })}
                        className="h-3 w-3"
                      />
                      Non disponibile
                    </label>
                  </div>
                  <Input
                    data-testid="client-email"
                    type="email"
                    value={form.email || ""}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={!form.email_unavailable && !(form.email || "").trim() ? "bg-rose-50/40 border-rose-200 focus-visible:border-rose-400" : ""}
                  />
                  {!form.email_unavailable && <p className="text-[10px] text-rose-600 mt-0.5 italic">⚠️ campo obbligatorio</p>}
                </div>
                <div>
                  <Label>Telefono</Label>
                  <div className="flex gap-1.5">
                    <Input
                      data-testid="client-phone"
                      value={form.phone || ""}
                      onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\s+/g, "") })}
                      onBlur={(e) => setForm({ ...form, phone: e.target.value.replace(/\s+/g, "") })}
                      placeholder="Es. 021234567"
                    />
                    {form.phone && form.phone.trim() && (
                      <WhatsAppQuickMenu
                        number={form.phone}
                        clientName={`${form.name || ""} ${form.surname || ""}`.trim()}
                        testid="client-phone-wa"
                      />
                    )}
                  </div>
                </div>
                <div>
                  <Label>Cellulare</Label>
                  {(() => {
                    const mv = form.mobile ? validateItalianMobile(form.mobile) : null;
                    const invalid = mv && !mv.ok;
                    const valid = mv && mv.ok;
                    return (
                      <>
                        <div className="flex gap-1.5">
                          <Input
                            data-testid="client-mobile"
                            value={form.mobile || ""}
                            onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\s+/g, "") })}
                            onBlur={(e) => setForm({ ...form, mobile: e.target.value.replace(/\s+/g, "") })}
                            placeholder="Es. 3331234567 oppure +393331234567"
                            className={invalid ? "border-rose-500 bg-rose-50/30" : valid ? "border-emerald-500 bg-emerald-50/30" : ""}
                          />
                          {form.mobile && form.mobile.trim() && (
                            <WhatsAppQuickMenu
                              number={form.mobile}
                              clientName={`${form.name || ""} ${form.surname || ""}`.trim()}
                              testid="client-mobile-wa"
                            />
                          )}
                        </div>
                        {invalid && <span className="text-[10px] text-rose-600 mt-0.5 block">{mv.message}</span>}
                        {valid && <span className="text-[10px] text-emerald-700 mt-0.5 block">✓ Numero valido</span>}
                      </>
                    );
                  })()}
                </div>
                  </div>
                </fieldset>

                {/* Sezione Residenza (ambra tenue) */}
                <fieldset className="rounded-lg border border-amber-200 bg-amber-50/40 p-3">
                  <legend className="px-2 text-[11px] font-semibold uppercase tracking-wider text-amber-700 bg-white rounded">
                    🏠 Residenza
                  </legend>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Città di residenza</Label>
                  <CityAutocomplete
                    testid="client-city"
                    value={form.city}
                    onChange={(v) => setForm({ ...form, city: (v || "").toUpperCase() })}
                    onSelect={(item) => setForm((f) => ({ ...f, city: (item.name || "").toUpperCase(), province: item.province, cap: item.cap || f.cap }))}
                    inputClassName="uppercase"
                  />
                </div>
                <div><Label>Provincia</Label><Input data-testid="client-province" value={form.province || ""} onChange={(e) => setForm({ ...form, province: e.target.value.toUpperCase().slice(0, 2) })} className="uppercase" maxLength={2} /></div>
                <div><Label>CAP <span className="text-xs text-slate-400 normal-case">(auto da città)</span></Label><Input data-testid="client-cap" value={form.cap || ""} onChange={(e) => setForm({ ...form, cap: e.target.value })} maxLength={5} /></div>
                <div className="col-span-2">
                  <Label>Indirizzo <span className="text-xs text-slate-400 normal-case">(via auto-completata)</span></Label>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-9">
                      <AddressAutocomplete
                        testid="client-address"
                        value={form.address}
                        city={form.city}
                        onChange={(v) => setForm({ ...form, address: (v || "").toUpperCase() })}
                        onSelect={(item) => setForm((f) => ({
                          ...f,
                          address: (item.road || "").toUpperCase(),
                          house_number: item.house_number || f.house_number,
                          cap: item.postcode || f.cap,
                          city: f.city || (item.city || "").toUpperCase(),
                        }))}
                        inputClassName="uppercase"
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        data-testid="client-house-number"
                        className="uppercase"
                        value={form.house_number || ""}
                        onChange={(e) => setForm({ ...form, house_number: e.target.value.toUpperCase() })}
                        placeholder="N° civico"
                      />
                    </div>
                  </div>
                </div>
                  </div>
                </fieldset>

                {/* Sezione Altri dati & note (viola tenue) */}
                <fieldset className="rounded-lg border border-violet-200 bg-violet-50/40 p-3">
                  <legend className="px-2 text-[11px] font-semibold uppercase tracking-wider text-violet-700 bg-white rounded">
                    📋 Altri dati & note
                  </legend>
                  {/* Banner informativo: dati patente estratti dall'AI (se non ancora confermati manualmente) */}
                  {(() => {
                    const aiAt = form.driving_license_ai_extracted_at;
                    const verifiedAt = form.driving_license_human_verified_at;
                    const showAi = !!aiAt && (!verifiedAt || new Date(verifiedAt) < new Date(aiAt));
                    const verifyNow = () => {
                      setForm({ ...form, driving_license_human_verified_at: new Date().toISOString() });
                      toast.success("Dati patente confermati manualmente");
                    };
                    if (showAi) {
                      return (
                        <div
                          data-testid="ai-patente-banner"
                          className="mb-2 -mt-1 flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-blue-50 border border-blue-200 text-[11px] text-blue-800"
                        >
                          <span className="flex items-center gap-1.5">
                            📋 Dati patente estratti dall&apos;AI il {new Date(aiAt).toLocaleString("it-IT")} — controlla e conferma se corretti.
                          </span>
                          <button
                            type="button"
                            data-testid="ai-patente-verify-btn"
                            onClick={verifyNow}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                            title="Conferma che i dati estratti sono corretti"
                          >
                            ✓ Conferma corretti
                          </button>
                        </div>
                      );
                    }
                    if (aiAt && verifiedAt) {
                      return (
                        <div
                          data-testid="ai-patente-verified-banner"
                          className="mb-2 -mt-1 inline-flex items-center gap-1.5 px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-800"
                        >
                          ✓ Dati patente verificati dall&apos;operatore il {new Date(verifiedAt).toLocaleString("it-IT")}
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="flex items-center gap-1.5">Data primo rilascio patente
                    {form.driving_license_ai_extracted_at && form.driving_license_issued_at && (
                      <span
                        data-testid="ai-badge-issued"
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-200"
                        title={`Compilato dall'AI il ${new Date(form.driving_license_ai_extracted_at).toLocaleString("it-IT")}`}
                      >📋 AI</span>
                    )}
                  </Label>
                  <DatePicker data-testid="client-license-date" value={form.driving_license_issued_at || ""} onChange={(e) => setForm({ ...form, driving_license_issued_at: e.target.value })} />
                </div>
                <div>
                  <Label className="flex items-center gap-1.5">🔄 Data ultimo rinnovo patente
                    {form.driving_license_ai_extracted_at && form.driving_license_renewed_at && (
                      <span
                        data-testid="ai-badge-renewed"
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-200"
                        title={`Compilato dall'AI il ${new Date(form.driving_license_ai_extracted_at).toLocaleString("it-IT")}`}
                      >📋 AI</span>
                    )}
                  </Label>
                  <DatePicker data-testid="client-license-renewed" value={form.driving_license_renewed_at || ""}
                    onChange={(e) => setForm({ ...form, driving_license_renewed_at: e.target.value })} />
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    Data dell&apos;ultima visita medica di conferma idoneità.
                  </p>
                </div>
                <div>
                  <Label className="flex items-center gap-1.5">🚦 Categoria patente</Label>
                  <select
                    data-testid="client-license-category"
                    value={form.driving_license_category || "B"}
                    onChange={(e) => setForm({ ...form, driving_license_category: e.target.value })}
                    className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm bg-white"
                  >
                    <option value="AM">AM (ciclomotore)</option>
                    <option value="A">A (moto)</option>
                    <option value="B">B (auto)</option>
                    <option value="C">C (autocarri)</option>
                    <option value="D">D (autobus)</option>
                    <option value="BE">BE (auto + rimorchio)</option>
                    <option value="CE">CE (autotreno)</option>
                    <option value="DE">DE (autobus + rimorchio)</option>
                  </select>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    Determina la durata di validità al rinnovo (norma italiana).
                  </p>
                </div>
                <div>
                  <Label className="flex items-center gap-1.5">🚗 Scadenza patente
                    {form.driving_license_expires_client_updated_at && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200" title={`Aggiornata dal cliente il ${formatDate(form.driving_license_expires_client_updated_at)}`}>
                        ✓ aggiornata dal cliente
                      </span>
                    )}
                    {form.driving_license_ai_extracted_at && form.driving_license_expires_at && (
                      <span
                        data-testid="ai-badge-expires"
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-200"
                        title={`Compilato dall'AI il ${new Date(form.driving_license_ai_extracted_at).toLocaleString("it-IT")}`}
                      >📋 AI</span>
                    )}
                    {(() => {
                      // 🆕 Calcolo automatico scadenza patente in base alla normativa italiana:
                      // - A, B, AM: <50 → 10y, 50-69 → 5y, 70-79 → 3y, 80+ → 2y
                      // - C: <65 → 5y, 65+ → 2y
                      // - D: <60 → 5y, 60+ → 1y
                      // La scadenza coincide sempre con il giorno+mese del compleanno del titolare.
                      const baseDate = form.driving_license_renewed_at || form.driving_license_issued_at;
                      if (!baseDate || !form.birth_date) return null;
                      const compute = () => {
                        // 🆕 28/07/2026 — Usa helper condiviso (normalizza categoria, gestisce CE/DE, fuso locale)
                        const res = computeLicenseExpiry({
                          category: form.driving_license_category,
                          birthDate: form.birth_date,
                          renewalOrIssueDate: baseDate,
                        });
                        if (!res) { toast.error("Date non valide"); return; }
                        // Il pulsante Calcola aggiorna SEMPRE il campo Scadenza patente, sovrascrivendo
                        // eventuali valori manuali/AI precedenti (audit di conferma azzerati).
                        setForm({
                          ...form,
                          driving_license_expires_at: res.iso,
                          driving_license_expires_client_updated_at: null,
                          driving_license_ai_extracted_at: null,
                        });
                        toast.success(`Scadenza calcolata: ${res.expected.toLocaleDateString("it-IT")} (${res.ruleLabel} · età al rinnovo ${res.ageAtRenewal})`);
                      };
                      return (
                        <button
                          type="button"
                          data-testid="client-license-compute-expiry"
                          onClick={compute}
                          className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 transition-colors"
                          title="Calcola la scadenza dalla data di rinnovo + età + categoria (normativa italiana). La scadenza coincide con il giorno del compleanno."
                        >
                          🧮 Calcola
                        </button>
                      );
                    })()}
                  </Label>
                  <DatePicker data-testid="client-license-expires" value={form.driving_license_expires_at || ""}
                    onChange={(e) => setForm({ ...form, driving_license_expires_at: e.target.value })} />
                  {form.driving_license_expires_at && (() => {
                    const t = new Date(); t.setHours(0,0,0,0);
                    const e = new Date(form.driving_license_expires_at); e.setHours(0,0,0,0);
                    const days = Math.ceil((e - t) / 86400000);
                    const cls = days < 0 ? "text-red-700 bg-red-50 border-red-200" : days <= 30 ? "text-amber-800 bg-amber-100 border-amber-300" : "text-emerald-700 bg-emerald-50 border-emerald-200";
                    const lbl = days < 0 ? `⚠️ Scaduta da ${Math.abs(days)} gg` : days === 0 ? "🔴 Scade oggi" : days <= 30 ? `⏰ Mancano ${days} gg` : `✅ Mancano ${days} gg`;
                    return <span className={`inline-block mt-1 mr-1.5 text-[11px] font-semibold px-2 py-0.5 rounded border ${cls}`}>{lbl}</span>;
                  })()}
                  {/* 🆕 Avviso discrepanza tra scadenza inserita e scadenza calcolata da norma */}
                  {form.driving_license_expires_at && form.birth_date && (form.driving_license_renewed_at || form.driving_license_issued_at) && (() => {
                    const baseDate = form.driving_license_renewed_at || form.driving_license_issued_at;
                    const res = computeLicenseExpiry({
                      category: form.driving_license_category,
                      birthDate: form.birth_date,
                      renewalOrIssueDate: baseDate,
                    });
                    if (!res) return null;
                    const expectedIso = res.iso;
                    const expected = res.expected;
                    if (expectedIso === form.driving_license_expires_at) {
                      return <span className="inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded border text-emerald-700 bg-emerald-50 border-emerald-200" title="La scadenza coincide con quella calcolata secondo norma.">✓ Conforme a norma</span>;
                    }
                    const diffMs = Math.abs(expected - new Date(form.driving_license_expires_at));
                    const diffDays = Math.round(diffMs / 86400000);
                    return (
                      <span className="inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded border text-rose-700 bg-rose-50 border-rose-200" title={`Scadenza attesa per norma: ${expected.toLocaleDateString("it-IT")} · Differenza: ${diffDays} giorni`}>
                        ⚠️ Discrepanza norma ({diffDays}gg)
                      </span>
                    );
                  })()}
                </div>
                <div className="col-span-2 -mt-1 flex items-center justify-between gap-3 flex-wrap">
                  <label className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer select-none" title="Se attivo, il sistema invia automaticamente promemoria a 60, 30 e 7 giorni prima della scadenza patente.">
                    <input
                      type="checkbox"
                      data-testid="client-notify-license-expiry"
                      checked={form.notify_license_expiry !== false}
                      onChange={(e) => setForm({ ...form, notify_license_expiry: e.target.checked })}
                      className="h-3.5 w-3.5 accent-amber-600"
                    />
                    Invia promemoria automatici scadenza patente <span className="text-[10px] text-slate-500 ml-1">(60 / 30 / 7 gg)</span>
                  </label>
                  {editing?.id && form.driving_license_expires_at && (
                    <button
                      type="button"
                      data-testid="client-send-license-expiry"
                      onClick={async () => {
                        try {
                          const { data } = await api.post(`/clients/${editing.id}/send-license-expiry-notice`);
                          if (data.email_sent) toast.success(`Avviso patente inviato a ${data.target_email}`);
                          else toast.info("Cliente senza email: usa il WhatsApp suggerito.");
                          if (data.whatsapp_url) window.open(data.whatsapp_url, "_blank", "noopener,noreferrer");
                        } catch (e) { toast.error(formatApiError(e)); }
                      }}
                      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-amber-400 bg-amber-100 hover:bg-amber-200 text-amber-800 font-semibold transition"
                    >
                      📧 Invia avviso ora
                    </button>
                  )}
                </div>
                <div>
                  <Label className="flex items-center gap-1.5">🆔 Scadenza carta d&apos;identità
                    {form.id_card_ai_extracted_at && form.id_card_expires_at && (
                      <span data-testid="ai-badge-idcard" className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-200" title={`Compilato dall'AI il ${new Date(form.id_card_ai_extracted_at).toLocaleString("it-IT")}`}>📋 AI</span>
                    )}
                  </Label>
                  <DatePicker data-testid="client-idcard-expires" value={form.id_card_expires_at || ""}
                    onChange={(e) => setForm({ ...form, id_card_expires_at: e.target.value })} />
                  <label className="mt-1 flex items-center gap-2 text-[11px] text-slate-700 cursor-pointer select-none" title="Se attivo, il sistema invia automaticamente promemoria a 60, 30 e 7 giorni prima della scadenza.">
                    <input
                      type="checkbox"
                      data-testid="client-notify-idcard-expiry"
                      checked={form.notify_id_card_expiry !== false}
                      onChange={(e) => setForm({ ...form, notify_id_card_expiry: e.target.checked })}
                      className="h-3.5 w-3.5 accent-amber-600"
                    />
                    Invia promemoria automatici <span className="text-[10px] text-slate-500">(60/30/7 gg)</span>
                  </label>
                </div>
                <div>
                  <Label className="flex items-center gap-1.5">💳 Scadenza tessera sanitaria
                    {form.health_card_ai_extracted_at && form.health_card_expires_at && (
                      <span data-testid="ai-badge-healthcard" className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-200" title={`Compilato dall'AI il ${new Date(form.health_card_ai_extracted_at).toLocaleString("it-IT")}`}>📋 AI</span>
                    )}
                  </Label>
                  <DatePicker data-testid="client-healthcard-expires" value={form.health_card_expires_at || ""}
                    onChange={(e) => setForm({ ...form, health_card_expires_at: e.target.value })} />
                  <label className="mt-1 flex items-center gap-2 text-[11px] text-slate-700 cursor-pointer select-none" title="Se attivo, il sistema invia automaticamente promemoria a 60, 30 e 7 giorni prima della scadenza.">
                    <input
                      type="checkbox"
                      data-testid="client-notify-healthcard-expiry"
                      checked={form.notify_health_card_expiry !== false}
                      onChange={(e) => setForm({ ...form, notify_health_card_expiry: e.target.checked })}
                      className="h-3.5 w-3.5 accent-amber-600"
                    />
                    Invia promemoria automatici <span className="text-[10px] text-slate-500">(60/30/7 gg)</span>
                  </label>
                </div>
                {/* Pulsante unico per inviare avviso documenti in scadenza secondo preferenze */}
                {editing?.id && (form.driving_license_expires_at || form.id_card_expires_at || form.health_card_expires_at) && (
                  <div className="col-span-2 -mt-1 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-[11px] text-amber-800">
                      📋 Invia al cliente un avviso unico con <strong>tutti i documenti in scadenza</strong> per i quali è attiva la preferenza qui sopra.
                    </div>
                    <button
                      type="button"
                      data-testid="client-send-documents-expiry"
                      onClick={async () => {
                        try {
                          const { data } = await api.post(`/clients/${editing.id}/send-document-expiry-notice`);
                          const includedLabels = (data.included || []).map((i) => i.label).join(", ") || "(nessuno)";
                          if (data.email_sent) toast.success(`Avviso inviato a ${data.target_email}\nIncluso: ${includedLabels}`);
                          else toast.info(`Cliente senza email — usa WhatsApp\nIncluso: ${includedLabels}`);
                          if (data.whatsapp_url) window.open(data.whatsapp_url, "_blank", "noopener,noreferrer");
                        } catch (e) { toast.error(formatApiError(e)); }
                      }}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-amber-500 bg-amber-200 hover:bg-amber-300 text-amber-900 font-semibold transition"
                    >
                      📋 Invia avviso documenti in scadenza
                    </button>
                  </div>
                )}
                <div className="col-span-2">
                  <Label className="text-amber-800 font-semibold">📝 Note</Label>
                  <Textarea
                    data-testid="client-notes"
                    value={form.notes || ""}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                    className="bg-amber-50/30 border-amber-200 focus:ring-amber-400 mt-1"
                    placeholder="Annotazioni libere sul cliente (es. preferenze, eventi rilevanti, follow-up…)"
                  />
                </div>
                  </div>
                </fieldset>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {editing && (
                  <ClientPoliciesSection policies={clientPolicies} onOpenPolicy={openPolicyFromClient} />
                )}

                {editing && (
                  <div className="col-span-2 border-t border-slate-200 pt-4 mt-2">
                    <Label className="text-sm font-semibold flex items-center gap-2">
                      <span className="relative inline-flex">
                        <FileBox className="h-4 w-4" />
                        {docs.length > 0 && (
                          <span data-testid="inline-docs-badge" className="absolute -top-1.5 -right-2 bg-blue-600 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                            {docs.length > 99 ? "99+" : docs.length}
                          </span>
                        )}
                      </span>
                      Allegati e documenti del cliente {docs.length > 0 && <span className="text-xs font-normal text-slate-500">({docs.length})</span>}
                    </Label>
                    <DropZone
                      onFiles={uploadDocsDrop}
                      accept={["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"]}
                      disabled={uploadingDoc}
                      testid="client-docs-inline-dropzone"
                      className="bg-blue-50/50 border border-blue-100 rounded-md p-3 mt-2 space-y-2"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Select value={docKind} onValueChange={setDocKind}>
                          <SelectTrigger data-testid="inline-doc-kind"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="carta_identita">Carta d&apos;identità</SelectItem>
                            <SelectItem value="patente">Patente</SelectItem>
                            <SelectItem value="passaporto">Passaporto</SelectItem>
                            <SelectItem value="tessera_sanitaria">Tessera sanitaria</SelectItem>
                            <SelectItem value="contratto">Contratto</SelectItem>
                            <SelectItem value="altro">Altro</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          data-testid="inline-doc-description"
                          value={docDesc}
                          onChange={(e) => setDocDesc(e.target.value)}
                          placeholder="Descrizione (cosa stai allegando)"
                        />
                      </div>
                      <label className="block">
                        <input data-testid="inline-doc-file" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" onChange={uploadDoc} className="hidden" disabled={uploadingDoc} />
                        <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border border-dashed ${uploadingDoc ? "border-slate-300 text-slate-400" : "border-blue-400 hover:bg-blue-100 cursor-pointer text-blue-700"} text-sm`}>
                          <Plus className="h-4 w-4" /> {uploadingDoc ? "Caricamento…" : "Carica documenti (PDF / JPG / PNG / WEBP / HEIC, max 10 MB, multipli) — oppure trascina qui"}
                        </span>
                      </label>
                    </DropZone>
                    <div className="space-y-2 mt-3" data-testid="inline-docs-list">
                      {docs.length === 0 && <p className="text-xs text-slate-400 text-center py-3">Nessun documento allegato.</p>}
                      {docs.map((a) => (
                        <AttachmentItem key={a.id} att={a} onDownload={downloadDoc} onDelete={removeDoc} onPreview={() => setLightboxIndex(docs.findIndex((d) => d.id === a.id))} onRequestSignature={requestSignatureForDoc} />
                      ))}
                    </div>
                  </div>
                )}

                <div className="col-span-2 border-t border-slate-200 pt-4 mt-2">
                  <Label className="text-sm font-semibold flex items-center gap-2"><Car className="h-4 w-4" /> Veicoli del cliente</Label>
                  <div className="space-y-2 mt-2" data-testid="vehicles-list">
                    {(form.vehicles || []).map((v, i) => (
                      <div key={`${v.plate}-${i}`} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm">
                        <span className="font-mono font-medium uppercase">{v.plate}</span>
                        <span className="text-slate-600">{v.brand} {v.model}</span>
                        {v.year && <span className="text-slate-400 text-xs">({v.year})</span>}
                        <button type="button" data-testid={`remove-vehicle-${i}`} onClick={() => removeVehicle(i)} className="ml-auto text-red-500 hover:text-red-700"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-12 gap-2 mt-3">
                    <div className="col-span-3 flex flex-col">
                      <PlateInput
                        testid="veh-plate"
                        value={newVeh.plate}
                        onChange={(v) => setNewVeh({ ...newVeh, plate: v })}
                        overrideFlag={!!newVeh.plate_override}
                        onOverrideChange={(v) => setNewVeh({ ...newVeh, plate_override: v })}
                      />
                    </div>
                    <VehicleAutocomplete
                      testid="veh-brand"
                      placeholder="Marca"
                      className="col-span-3"
                      value={newVeh.brand}
                      onChange={(v) => setNewVeh({ ...newVeh, brand: v })}
                      suggestions={brandSuggestions}
                    />
                    <VehicleAutocomplete
                      testid="veh-model"
                      placeholder="Modello"
                      className="col-span-3"
                      value={newVeh.model}
                      onChange={(v) => setNewVeh({ ...newVeh, model: v })}
                      suggestions={modelSuggestions}
                    />
                    <Input data-testid="veh-year" placeholder="Anno" type="number" className="col-span-2" value={newVeh.year} onChange={(e) => setNewVeh({ ...newVeh, year: e.target.value })} />
                    <Button type="button" data-testid="add-vehicle-btn" onClick={addVehicle} className="col-span-1 bg-blue-600 hover:bg-blue-700 px-2"><Plus className="h-4 w-4" /></Button>
                  </div>
                </div>

                {/* 🆕 Feb 2026 — Area privata condivisa (uni-direzionale, reversibile) */}
                {editing?.id && (
                  <div className="col-span-2 border-t border-slate-200 pt-4 mt-2">
                    <Label className="text-sm font-semibold flex items-center gap-2">
                      👨‍👩‍👧 Area privata condivisa
                      <span className="text-[10px] font-normal text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5">nuovo</span>
                    </Label>
                    <p className="text-[11px] text-slate-500 mt-1">
                      Elenco di clienti le cui polizze verranno mostrate <strong>anche</strong> nell&apos;area privata di <strong>{form.surname} {form.name}</strong>.
                      Utile quando il capo-famiglia gestisce le polizze di moglie/figli. Le polizze collegate mostreranno chiaramente l&apos;intestatario. Reversibile in qualsiasi momento.
                    </p>
                    <div className="mt-3 space-y-2" data-testid="portal-links-list">
                      {portalLinks.length === 0 && (
                        <div className="text-xs text-slate-400 italic border border-dashed border-slate-200 rounded-md px-3 py-2">Nessun cliente collegato.</div>
                      )}
                      {portalLinks.map((lk) => (
                        <div key={lk.id} className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-md px-3 py-2 text-sm" data-testid={`portal-link-${lk.id}`}>
                          <span className="text-violet-700">🔗</span>
                          <span className="font-medium">{lk.surname} {lk.name}</span>
                          {lk.email && <span className="text-slate-500 text-xs truncate">· {lk.email}</span>}
                          <button
                            type="button"
                            data-testid={`remove-portal-link-${lk.id}`}
                            onClick={() => removePortalLink(lk.id)}
                            className="ml-auto text-red-500 hover:text-red-700"
                            title="Rimuovi collegamento"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3">
                      <Label className="text-xs text-slate-600">Aggiungi cliente</Label>
                      <div className="relative mt-1">
                        <Input
                          data-testid="portal-link-search"
                          placeholder="Cerca per cognome, nome, email o cellulare…"
                          value={portalLinkSearch}
                          onChange={(e) => setPortalLinkSearch(e.target.value)}
                        />
                        {portalLinkSearch.trim().length >= 2 && (
                          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-y-auto" data-testid="portal-link-suggestions">
                            {(() => {
                              const q = portalLinkSearch.trim().toLowerCase();
                              const linkedIds = new Set([editing.id, ...portalLinks.map((l) => l.id)]);
                              const matches = items.filter((c) => {
                                if (linkedIds.has(c.id)) return false;
                                const hay = `${c.surname || ""} ${c.name || ""} ${c.email || ""} ${c.mobile || ""} ${c.phone || ""} ${c.fiscal_code || ""}`.toLowerCase();
                                return hay.includes(q);
                              }).slice(0, 8);
                              if (matches.length === 0) {
                                return <div className="px-3 py-2 text-xs text-slate-400 italic">Nessun risultato</div>;
                              }
                              return matches.map((c) => (
                                <button
                                  key={c.id}
                                  type="button"
                                  data-testid={`portal-link-suggestion-${c.id}`}
                                  onClick={() => addPortalLink(c)}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-violet-50 border-b border-slate-100 last:border-b-0"
                                >
                                  <div className="font-medium">{c.surname} {c.name}</div>
                                  <div className="text-[11px] text-slate-500">
                                    {c.email && <span>📧 {c.email}</span>}
                                    {c.mobile && <span className="ml-2">📱 {c.mobile}</span>}
                                    {c.fiscal_code && <span className="ml-2 font-mono">{c.fiscal_code}</span>}
                                  </div>
                                </button>
                              ));
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {editing?.id && <ClientGdprPanel clientId={editing.id} canExport={authUser?.role === "admin"} />}
              </div>
              </div>
              <DialogFooter>
                {editing?.id && (
                  <span className="text-[11px] text-slate-500 mr-auto inline-flex items-center gap-1" data-testid="client-autosave-indicator">
                    {autoSaveAt
                      ? <>💾 Bozza salvata automaticamente alle {autoSaveAt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</>
                      : <>💾 Autosave attivo (ogni 30s)</>}
                  </span>
                )}
                <Button variant="outline" onClick={closeClientDialog}>Annulla</Button>
                <Button data-testid="save-client-btn" disabled={loading || (form.subject_type === 'company' ? (!(form.ragione_sociale || '').trim()) : (!form.name || !form.surname))} onClick={submit} className="bg-[#0F172A] hover:bg-[#1E293B]">
                  {loading ? "Salvataggio…" : "Salva"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="clients-search" className="pl-9" placeholder="Cerca per nome, email, CF, targa…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-slate-500 whitespace-nowrap">Ordina per</span>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="clients-sort" className="w-[230px] h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="surname_asc">Cognome A → Z (default)</SelectItem>
              <SelectItem value="surname_desc">Cognome Z → A</SelectItem>
              <SelectItem value="name_asc">Nome A → Z</SelectItem>
              <SelectItem value="name_desc">Nome Z → A</SelectItem>
              <SelectItem value="created_desc">Aggiunti più di recente</SelectItem>
              <SelectItem value="created_asc">Aggiunti meno di recente</SelectItem>
              <SelectItem value="birth_desc">Più giovani prima</SelectItem>
              <SelectItem value="birth_asc">Più anziani prima</SelectItem>
              <SelectItem value="city_asc">Città A → Z</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <span className="text-xs text-slate-400 ml-auto">{filtered.length} {filtered.length === 1 ? "cliente" : "clienti"}</span>
      </div>

      {/* 🆕 Feb 2026 — Filtro Tipo Soggetto (Privato/Azienda) */}
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="clients-subject-type-filter">
        <span className="text-[11px] uppercase font-semibold text-slate-500 tracking-wider">Tipo soggetto:</span>
        {[
          { v: "all", l: "Tutti", icon: "👥" },
          { v: "private", l: "Privati", icon: "👤" },
          { v: "company", l: "Aziende", icon: "🏢" },
        ].map((opt) => {
          const active = subjectTypeFilter === opt.v;
          const testid = `clients-subject-type-${opt.v}`;
          return (
            <button
              key={opt.v}
              type="button"
              data-testid={testid}
              onClick={() => setSubjectTypeFilter(opt.v)}
              className={`text-xs px-3 py-1.5 rounded-md border transition-all flex items-center gap-1.5 ${active ? "bg-blue-600 border-blue-600 text-white shadow" : "bg-white border-slate-200 text-slate-600 hover:border-blue-300"}`}
            >
              <span>{opt.icon}</span>
              <span>{opt.l}</span>
              {active && opt.v !== "all" && (
                <span className="text-[10px] bg-blue-500 rounded px-1 py-0.5">
                  {items.filter((c) => (c.subject_type || "private") === opt.v).length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* 🆕 Filtri "anomalie" attivi (visibili solo quando applicati dalla Dashboard) */}
      {(noCFFilter || noPortalFilter) && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="clients-anomaly-filters">
          {noCFFilter && (
            <button
              type="button"
              data-testid="clients-filter-no-cf-clear"
              onClick={() => setNoCFFilter(false)}
              className="text-xs uppercase tracking-wide px-3 py-1.5 rounded-md border transition-all flex items-center gap-1.5 border-rose-600 bg-rose-500 text-white shadow hover:bg-rose-600"
              title="Rimuovi filtro anomalia — Clienti senza codice fiscale"
            >
              <span>🆔</span>
              <span>Senza CF</span>
              <span className="text-[11px] leading-none opacity-80">✕</span>
            </button>
          )}
          {noPortalFilter && (
            <button
              type="button"
              data-testid="clients-filter-no-portal-clear"
              onClick={() => setNoPortalFilter(false)}
              className="text-xs uppercase tracking-wide px-3 py-1.5 rounded-md border transition-all flex items-center gap-1.5 border-slate-600 bg-slate-500 text-white shadow hover:bg-slate-600"
              title="Rimuovi filtro anomalia — Clienti senza area riservata"
            >
              <span>🔑</span>
              <span>Senza portale</span>
              <span className="text-[11px] leading-none opacity-80">✕</span>
            </button>
          )}
        </div>
      )}

      <div className="hidden print:block mb-6">
        <h1 className="font-display text-2xl font-semibold">Anagrafica Clienti — Assicura</h1>
        <p className="text-xs text-slate-500">{new Date().toLocaleDateString("it-IT")}</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
        {selectedIds.size > 0 && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-center justify-between gap-3 print:hidden" data-testid="bulk-action-bar">
            <span className="text-sm text-amber-900 font-medium">
              {selectedIds.size} client{selectedIds.size === 1 ? "e selezionato" : "i selezionati"}
            </span>
            <div className="flex items-center gap-2">
              <button
                data-testid="bulk-clear"
                onClick={() => setSelectedIds(new Set())}
                className="text-xs px-3 py-1.5 rounded border border-slate-200 hover:bg-white"
              >
                Annulla selezione
              </button>
              <button
                data-testid="bulk-delete"
                onClick={bulkDelete}
                className="text-xs px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white font-semibold inline-flex items-center gap-1.5"
              >
                <Trash2 className="h-3.5 w-3.5" /> Elimina selezionati
              </button>
            </div>
          </div>
        )}
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-blue-600 text-white text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-2 py-2 w-10 print:hidden">
                <input
                  type="checkbox"
                  data-testid="select-all"
                  checked={filtered.length > 0 && filtered.every((c) => selectedIds.has(c.id))}
                  onChange={toggleSelectAllFiltered}
                  className="h-4 w-4 rounded border-slate-300 cursor-pointer"
                />
              </th>
              <th className="text-left px-2 py-2">Cliente</th>
              <th className="text-left px-2 py-2">Contatti</th>
              <th className="text-left px-2 py-2">CF · Nascita</th>
              <th className="text-left px-2 py-2">Località</th>
              <th className="text-left px-2 py-2">Area riservata</th>
              <th className="text-right px-2 py-2 print:hidden">Azioni</th>
            </tr>
          </thead>
          <tbody data-testid="clients-table">
            {filtered.length === 0 && (
              <tr><td colSpan="7" className="text-center py-10 text-slate-400">Nessun cliente trovato.</td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} id={`client-${c.id}`} className={`border-t border-slate-100 hover:bg-slate-50 scroll-mt-32 ${selectedIds.has(c.id) ? "bg-blue-50/30" : ""}`}>
                <td className="px-2 py-2 w-10 print:hidden">
                  <input
                    type="checkbox"
                    data-testid={`select-client-${c.id}`}
                    checked={selectedIds.has(c.id)}
                    onChange={() => toggleSelect(c.id)}
                    className="h-4 w-4 rounded border-slate-300 cursor-pointer"
                  />
                </td>
                <td className="px-2 py-2">
                  <div
                    className="font-medium flex items-center gap-1.5 cursor-pointer hover:text-blue-700 select-none uppercase"
                    onDoubleClick={() => openEdit(c)}
                    data-testid={`client-name-${c.id}`}
                    title="Doppio click per modificare"
                  >
                    {c.subject_type === "company" ? (
                      <>
                        <span className="text-blue-600" title="Ragione Sociale (Azienda)">{c.ragione_sociale || c.name}</span>
                        <span
                          data-testid={`client-badge-company-${c.id}`}
                          className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 border border-indigo-300"
                          title={c.partita_iva ? `P.IVA ${c.partita_iva}` : "Azienda / Persona Giuridica"}
                        >
                          🏢 Azienda
                        </span>
                      </>
                    ) : (
                      <>
                        {c.surname} {c.name}
                      </>
                    )}
                    <NotesHoverIcon notes={c.notes} testid={`client-notes-icon-${c.id}`} />
                    {/* 🆕 Feb 2026 — Badge polizze totali del cliente (con tooltip attive/totali) */}
                    {(c.total_policies_count || 0) > 0 && (
                      <span
                        data-testid={`client-policies-badge-${c.id}`}
                        title={`${c.active_policies_count || 0} attiv${c.active_policies_count === 1 ? "a" : "e"} su ${c.total_policies_count} tot${c.total_policies_count === 1 ? "ale" : "ali"}`}
                        className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-300"
                      >
                        📄 {c.total_policies_count}
                        <span className="text-blue-600 normal-case">poliz{c.total_policies_count === 1 ? "za" : "ze"}</span>
                      </span>
                    )}
                    {/* 🆕 Badge sinistri: appare solo se il cliente ha almeno 1 sinistro */}
                    {(c.claims_count || 0) > 0 && (
                      <span
                        data-testid={`client-claims-badge-${c.id}`}
                        title={`${c.claims_count} sinistr${c.claims_count === 1 ? "o" : "i"} registrat${c.claims_count === 1 ? "o" : "i"}`}
                        className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-300"
                      >
                        ⚠️ {c.claims_count}
                        <span className="text-red-700 normal-case">sinistr{c.claims_count === 1 ? "o" : "i"}</span>
                      </span>
                    )}
                    {/* 🆕 Spia rossa se scadenza patente discorda dalla norma */}
                    {(() => {
                      if (!c.driving_license_expires_at || !c.birth_date) return null;
                      const baseDate = c.driving_license_renewed_at || c.driving_license_issued_at;
                      if (!baseDate) return null;
                      // 🆕 28/07/2026 — Usa helper condiviso (fuso locale, CE→C, DE→D)
                      const res = computeLicenseExpiry({
                        category: c.driving_license_category,
                        birthDate: c.birth_date,
                        renewalOrIssueDate: baseDate,
                      });
                      if (!res) return null;
                      const expected = res.expected;
                      const expectedIso = res.iso;
                      if (expectedIso === c.driving_license_expires_at) return null;
                      return (
                        <span
                          data-testid={`license-discrepancy-${c.id}`}
                          className="ml-1.5 inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-300"
                          title={`Scadenza patente non conforme alla norma. Attesa: ${expected.toLocaleDateString("it-IT")}`}
                        >
                          🚗⚠️
                        </span>
                      );
                    })()}
                  </div>
                  <div className="text-xs text-slate-500">{c.id.slice(0, 8)}</div>
                </td>
                <td className="px-2 py-2 text-slate-600">
                  {c.email && (
                    <div className="flex items-center gap-1">
                      <a
                        href={`mailto:${c.email}`}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`email-${c.id}`}
                        className="flex items-center gap-1.5 text-xs text-blue-700 hover:text-blue-900 hover:underline"
                        title={`Invia email a ${c.email}`}
                      >
                        <Mail className="h-3.5 w-3.5 text-blue-600 shrink-0" />{c.email}
                      </a>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(c.email, "Email"); }}
                        className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded px-0.5 text-[10px] cursor-pointer"
                        title="Copia email negli appunti"
                        data-testid={`email-copy-${c.id}`}
                      >📋</button>
                    </div>
                  )}
                  {c.phone && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <a
                        href={`tel:${c.phone.replace(/\s+/g, "")}`}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`phone-${c.id}`}
                        className="flex items-center gap-1.5 text-xs text-amber-800 hover:text-amber-900 hover:underline"
                        title={`Chiama ${c.phone}`}
                      >
                        <Phone className="h-3.5 w-3.5 text-amber-600 shrink-0" />{c.phone}
                      </a>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(c.phone, "Telefono"); }}
                        className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded px-0.5 text-[10px] cursor-pointer"
                        title="Copia telefono negli appunti"
                        data-testid={`phone-copy-${c.id}`}
                      >📋</button>
                    </div>
                  )}
                  {c.mobile && (
                    <div className="flex items-center gap-1.5 mt-0.5 text-xs">
                      <a
                        href={`tel:${c.mobile.replace(/\s+/g, "")}`}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`mobile-${c.id}`}
                        className="flex items-center gap-1.5 text-purple-800 hover:text-purple-900 hover:underline"
                        title={`Chiama ${c.mobile}`}
                      >
                        <Smartphone className="h-3.5 w-3.5 text-purple-600 shrink-0" />
                        <span>{c.mobile}</span>
                      </a>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(c.mobile, "Cellulare"); }}
                        className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded px-0.5 text-[10px] cursor-pointer"
                        title="Copia cellulare negli appunti"
                        data-testid={`mobile-copy-${c.id}`}
                      >📋</button>
                      <WhatsAppButton
                        rawPhone={c.mobile}
                        defaultMessage={`Ciao ${c.name || ""}, ti scrivo da Studio di Consulenza Amoroso.`}
                        testid={`whatsapp-${c.id}`}
                      >
                        <span
                          className="inline-flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-full h-5 w-5 text-[10px] font-bold ml-1 transition-colors cursor-pointer"
                          title={`Apri WhatsApp con ${c.surname} ${c.name}`}
                        >💬</span>
                      </WhatsAppButton>
                    </div>
                  )}
                </td>
                <td className="px-2 py-2 text-slate-600 text-xs">
                  <div className="font-mono tabular-nums">{c.fiscal_code || "—"}</div>
                  <div className="text-slate-400 mt-0.5">
                    {c.birth_date ? formatDate(c.birth_date) : "—"}
                    {c.birth_date && computeAge(c.birth_date) !== null && (
                      <span className="ml-1 text-blue-700 font-semibold">· {computeAge(c.birth_date)} anni</span>
                    )}
                    {c.gender ? <span className="ml-1">· {c.gender === "M" ? "♂ Maschio" : "♀ Femmina"}</span> : ""}
                    {c.birth_place ? ` · ${c.birth_place}` : ""}
                  </div>
                  {c.marital_status && (
                    <div className="text-slate-500 mt-0.5" data-testid={`client-card-marital-${c.id}`}>💍 {maritalLabelMap[c.marital_status] || c.marital_status}</div>
                  )}
                  {c.profession && (
                    <div className="text-slate-500 mt-0.5" data-testid={`client-card-profession-${c.id}`}>💼 {professionLabelMap[c.profession] || c.profession}</div>
                  )}
                </td>
                <td className="px-2 py-2 text-slate-600 text-xs">
                  {c.city || "—"}{c.cap ? ` · ${c.cap}` : ""}
                </td>
                <td className="px-2 py-2 text-xs">
                  {c.portal_enabled ? (
                    <div className="space-y-1">
                      <span className="inline-flex items-center rounded-full border bg-emerald-50 border-emerald-200 text-emerald-700 px-2 py-0.5 font-medium">
                        Attiva
                      </span>
                      <div className="flex gap-1.5 mt-1">
                        <button
                          data-testid={`reset-portal-${c.id}`}
                          onClick={() => resetPortalPassword(c)}
                          className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                          title="Reimposta password"
                        >
                          <KeyRound className="h-3 w-3" /> Reset
                        </button>
                        <button
                          data-testid={`disable-portal-${c.id}`}
                          onClick={() => disablePortal(c)}
                          className="text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                          title="Disattiva"
                        >
                          <ShieldOff className="h-3 w-3" /> Off
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      data-testid={`enable-portal-${c.id}`}
                      onClick={() => enablePortal(c)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-slate-700"
                    >
                      <KeyRound className="h-3 w-3" /> Abilita
                    </button>
                  )}
                </td>
                <td className="px-2 py-2 text-right print:hidden">
                  <div className="inline-flex items-center gap-0.5">
                    <button data-testid={`view-contact-${c.id}`} onClick={() => setContactView(c)} className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded p-1 transition-colors" title="Vedi contatti"><Eye className="h-4 w-4" /></button>
                    <button data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)} className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded p-1 transition-colors" title="Modifica"><Pencil className="h-4 w-4" /></button>
                    <button
                      data-testid={`send-message-${c.id}`}
                      onClick={() => setMsgTarget(c)}
                      className="text-green-600 hover:text-green-800 hover:bg-green-50 rounded p-1 transition-colors"
                      title="Invia messaggio (Email + WhatsApp) con template personalizzabili"
                    >
                      <MessageSquareText className="h-4 w-4" />
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`actions-menu-${c.id}`}
                          className="p-1 rounded hover:bg-slate-100 border border-slate-200 text-slate-600 transition-colors ml-0.5"
                          title="Altre azioni"
                          aria-label="Altre azioni"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56" data-testid={`actions-menu-content-${c.id}`}>
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">
                          Azioni cliente
                        </DropdownMenuLabel>
                        <DropdownMenuItem
                          onClick={() => { setDocsOpen(c); loadDocs(c.id); loadDocsSendLogs(c.id); setDocsSelectedEmail({}); }}
                          data-testid={`menu-docs-client-${c.id}`}
                        >
                          <FileBox className="mr-2 h-4 w-4 text-slate-600" /> Documenti
                          {c.documents_count > 0 && (
                            <span className="ml-auto bg-blue-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center">
                              {c.documents_count > 99 ? "99+" : c.documents_count}
                            </span>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => sendPortalAccess(c)}
                          data-testid={`menu-send-portal-${c.id}`}
                        >
                          <Send className="mr-2 h-4 w-4 text-indigo-600" />
                          {c.portal_enabled ? "Invia link area riservata" : "Attiva area riservata"}
                        </DropdownMenuItem>
                        {c.portal_enabled && (
                          <DropdownMenuItem
                            onClick={() => openMagicLinkMenu(c)}
                            data-testid={`menu-magic-link-${c.id}`}
                          >
                            <Zap className="mr-2 h-4 w-4 text-emerald-600" /> Accesso Rapido (Magic Link)
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => setTimelineClient(c)}
                          data-testid={`menu-timeline-${c.id}`}
                        >
                          <Clock className="mr-2 h-4 w-4 text-violet-600" /> Cronologia attività
                        </DropdownMenuItem>
                        {c.portal_enabled && (
                          <DropdownMenuItem
                            onClick={() => sendTestPush(c)}
                            data-testid={`menu-push-test-${c.id}`}
                          >
                            <Bell className="mr-2 h-4 w-4 text-amber-600" /> Notifica di test al portale
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => remove(c)}
                          className="text-red-600 focus:text-red-700 focus:bg-red-50"
                          data-testid={`menu-delete-client-${c.id}`}
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> Elimina cliente
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 🆕 09/07/2026 — Magic Link (Accesso Rapido) modal */}
      <Dialog open={!!magicLinkClient} onOpenChange={(o) => { if (!o) setMagicLinkClient(null); }}>
        <DialogContent data-testid="magic-link-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-emerald-600" />
              Invia Accesso Rapido
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Invia un link magico a <strong>{magicLinkClient?.surname} {magicLinkClient?.name}</strong> per accedere alla propria Area Riservata con un solo click, senza password.
            </p>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 space-y-1">
              <div>Il link è valido <strong>20 minuti</strong>.</div>
              <div>Se scade, il cliente può richiederne uno nuovo inserendo il suo Codice Fiscale su <code className="text-indigo-700 bg-white rounded px-1">/portale-clienti</code>.</div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                type="button"
                onClick={() => sendMagicLink("email")}
                disabled={!magicLinkClient?.email || magicLinkSending !== null}
                className="h-16 rounded-xl border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center gap-1 text-blue-800 transition-colors"
                title={magicLinkClient?.email || "Il cliente non ha email"}
                data-testid="magic-send-email"
              >
                {magicLinkSending === "email" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-6 w-6" />}
                <span className="text-xs font-semibold">Email</span>
                {magicLinkClient?.email && <span className="text-[10px] text-blue-600 truncate max-w-[140px]">{magicLinkClient.email}</span>}
              </button>
              <button
                type="button"
                onClick={() => sendMagicLink("whatsapp")}
                disabled={!(magicLinkClient?.mobile || magicLinkClient?.phone) || magicLinkSending !== null}
                className="h-16 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center gap-1 text-emerald-800 transition-colors"
                title={(magicLinkClient?.mobile || magicLinkClient?.phone) || "Il cliente non ha telefono"}
                data-testid="magic-send-whatsapp"
              >
                {magicLinkSending === "whatsapp" ? <Loader2 className="h-5 w-5 animate-spin" /> : <span className="text-2xl">💬</span>}
                <span className="text-xs font-semibold">WhatsApp</span>
                {(magicLinkClient?.mobile || magicLinkClient?.phone) && <span className="text-[10px] text-emerald-700">{magicLinkClient?.mobile || magicLinkClient?.phone}</span>}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Portal credentials modal */}
      <Dialog open={!!portalCreds} onOpenChange={(o) => { if (!o) setPortalCreds(null); }}>
        <DialogContent data-testid="portal-creds-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-blue-600" />
              {portalCreds?.kind === "reset" ? "Nuova password generata" : "Area riservata attivata"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Credenziali per <span className="font-medium">{portalCreds?.clientName}</span>. Comunicale al cliente: la password <strong>non verrà più mostrata</strong>.
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-4 font-mono text-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-slate-500 text-xs uppercase tracking-wide font-sans">Email</span>
                <span data-testid="portal-creds-email">{portalCreds?.email}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500 text-xs uppercase tracking-wide font-sans">Password</span>
                <span data-testid="portal-creds-password" className="text-blue-700 font-semibold">{portalCreds?.password}</span>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
              Suggerisci al cliente di custodire la password con cura: non verrà più mostrata.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button
                data-testid="send-creds-email-btn"
                variant="outline"
                onClick={sendCredsEmail}
                disabled={!portalCreds?.clientEmail}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                <Mail className="h-4 w-4 mr-1.5" /> Invia via email
              </Button>
              <Button
                data-testid="send-creds-whatsapp-btn"
                variant="outline"
                onClick={openCredsWhatsApp}
                disabled={!portalCreds?.clientMobile}
                className="border-green-300 text-green-700 hover:bg-green-50"
              >
                💬 Apri WhatsApp
              </Button>
            </div>
            <div className="flex gap-2 justify-end">
              <Button data-testid="copy-creds-btn" variant="outline" onClick={copyCreds}>
                {copied ? <><Check className="h-4 w-4 mr-2" /> Copiate</> : <><Copy className="h-4 w-4 mr-2" /> Copia</>}
              </Button>
              <Button data-testid="close-creds-btn" onClick={() => setPortalCreds(null)} className="bg-[#0F172A] hover:bg-[#1E293B]">Ho preso nota</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* === DIALOG VEDI CONTATTI === */}
      <Dialog open={!!contactView} onOpenChange={(o) => !o && setContactView(null)}>
        <DialogContent className="w-[95vw] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-emerald-600" /> Contatti cliente</DialogTitle>
          </DialogHeader>
          {contactView && (
            <div className="space-y-3" data-testid="client-contact-modal">
              <div className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
                {contactView.surname} {contactView.name}
                {contactView.fiscal_code && <div className="font-mono text-xs text-slate-500 font-normal mt-1">{contactView.fiscal_code}</div>}
              </div>
              {contactView.email ? (
                <a href={`mailto:${contactView.email}`} className="flex items-center gap-3 p-2.5 rounded-md border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors no-underline">
                  <Mail className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <span className="text-sm text-blue-900 break-all">{contactView.email}</span>
                </a>
              ) : <div className="flex items-center gap-3 p-2.5 text-xs text-slate-400 italic"><Mail className="h-5 w-5" /> Nessuna email</div>}
              {contactView.phone ? (
                <a href={`tel:${contactView.phone}`} className="flex items-center gap-3 p-2.5 rounded-md border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors no-underline">
                  <Phone className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <span className="text-sm text-amber-900">{contactView.phone}</span>
                </a>
              ) : <div className="flex items-center gap-3 p-2.5 text-xs text-slate-400 italic"><Phone className="h-5 w-5" /> Nessun telefono</div>}
              {contactView.mobile ? (
                <div className="flex items-center gap-2">
                  <a href={`tel:${contactView.mobile}`} className="flex-1 flex items-center gap-3 p-2.5 rounded-md border border-purple-200 bg-purple-50 hover:bg-purple-100 transition-colors no-underline">
                    <Smartphone className="h-5 w-5 text-purple-600 flex-shrink-0" />
                    <span className="text-sm text-purple-900">{contactView.mobile}</span>
                  </a>
                  <WhatsAppButton
                    rawPhone={contactView.mobile}
                    defaultMessage={`Ciao ${contactView.name || ""}, ti scrivo da Studio di Consulenza Amoroso.`}
                    testid={`whatsapp-contact-${contactView.id || "view"}`}
                  >
                    <span className="px-3 py-2.5 rounded-md bg-green-500 hover:bg-green-600 text-white font-semibold cursor-pointer" title="Apri WhatsApp">
                      💬
                    </span>
                  </WhatsAppButton>
                </div>
              ) : <div className="flex items-center gap-3 p-2.5 text-xs text-slate-400 italic"><Smartphone className="h-5 w-5" /> Nessun cellulare</div>}
              {(contactView.address || contactView.city) && (
                <div className="flex items-start gap-3 p-2.5 rounded-md border border-slate-200 bg-slate-50">
                  <MapPin className="h-5 w-5 text-slate-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-700">
                    {contactView.address} {contactView.house_number}<br />
                    {contactView.cap} {contactView.city} {contactView.province ? `(${contactView.province})` : ""}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Client documents dialog */}
      <Dialog open={!!docsOpen} onOpenChange={(o) => { if (!o) { setDocsOpen(null); setDocs([]); setDocDesc(""); setDocKind("altro"); setDocsSelectedEmail({}); setDocsSendLogs([]); setDocsPendingFlags({}); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-docs-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="relative inline-flex">
                <FileBox className="h-5 w-5 text-blue-600" />
                {docs.length > 0 && (
                  <span data-testid="docs-dialog-badge" className="absolute -top-1 -right-2 bg-blue-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                    {docs.length > 99 ? "99+" : docs.length}
                  </span>
                )}
              </span>
              <span>Documenti ({docs.length}) · {docsOpen?.surname} {docsOpen?.name}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <DropZone
              testid="dropzone-client-doc"
              onFiles={uploadDocsDrop}
              accept={["application/pdf", "image/jpeg", "image/png", "image/webp"]}
              multiple
              disabled={uploadingDoc}
              className="bg-blue-50/50 border border-blue-100 rounded-md p-4 space-y-3"
            >
              <Label className="text-xs uppercase tracking-wide text-blue-700">Carica nuovo documento</Label>
              <p className="text-[11px] text-blue-700">💡 Trascina i file qui per caricarli istantaneamente.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Select value={docKind} onValueChange={setDocKind}>
                  <SelectTrigger data-testid="doc-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="carta_identita">Carta d&apos;identità</SelectItem>
                    <SelectItem value="patente">Patente</SelectItem>
                    <SelectItem value="passaporto">Passaporto</SelectItem>
                    <SelectItem value="tessera_sanitaria">Tessera sanitaria</SelectItem>
                    <SelectItem value="contratto">Contratto</SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  data-testid="doc-description"
                  value={docDesc}
                  onChange={(e) => setDocDesc(e.target.value)}
                  placeholder="Cosa stai allegando? (descrizione)"
                />
              </div>
              <label className="block">
                <input data-testid="doc-file-input" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" onChange={uploadDoc} className="hidden" disabled={uploadingDoc} />
                <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border border-dashed ${uploadingDoc ? "border-slate-300 text-slate-400" : "border-blue-400 hover:bg-blue-100 cursor-pointer text-blue-700"} text-sm`}>
                  <Plus className="h-4 w-4" /> {uploadingDoc ? "Caricamento…" : "Scegli file (PDF / JPG / PNG / WEBP / HEIC, max 10 MB, multipli)"}
                </span>
              </label>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[11px] text-slate-500">oppure</span>
                <ScanButton
                  testid="scan-client-doc"
                  label="Importa da scanner"
                  variant="outline"
                  disabled={uploadingDoc}
                  onFile={(f) => {
                    // Riusa la stessa logica di uploadDoc tramite un evento sintetico
                    const fakeEvent = { target: { files: [f], value: "" } };
                    uploadDoc(fakeEvent);
                  }}
                />
                <CameraButton
                  testid="cam-scan-client-doc"
                  label="📷 Foto da fotocamera"
                  variant="outline"
                  disabled={uploadingDoc}
                  onFile={(f) => {
                    const fakeEvent = { target: { files: [f], value: "" } };
                    uploadDoc(fakeEvent);
                  }}
                />
              </div>
            </DropZone>
            <div className="space-y-2" data-testid="client-docs-list">
              <div className="flex items-center justify-between gap-2 pb-1 border-b border-slate-100">
                <div className="text-xs text-slate-500 inline-flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  Seleziona uno o più documenti per inviarli via email
                </div>
                <Button
                  data-testid="send-docs-btn"
                  onClick={openDocsSendDialog}
                  disabled={!Object.values(docsSelectedEmail).some(Boolean)}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs"
                >
                  <Mail className="h-3.5 w-3.5 mr-1.5" />
                  Invia via email ({Object.values(docsSelectedEmail).filter(Boolean).length})
                </Button>
              </div>
              {docs.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Nessun documento.</p>}
              {docs.length > 0 && (
                <div className="mb-2 flex items-center justify-between gap-2 flex-wrap p-2 rounded-md bg-blue-50 border border-blue-200 text-xs">
                  <span className="text-blue-800">
                    <strong>Gestione flag</strong>: attiva Visibile/Notifica, poi salva.
                    <span className="text-slate-500 ml-1">Nessuna email fino al click su &quot;Invia notifiche&quot;.</span>
                  </span>
                  <div className="flex items-center gap-2">
                    {docsFlagsDirty && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="docs-save-flags-btn"
                        onClick={saveDocFlags}
                        disabled={docsSavingFlags}
                        className="h-7 text-xs border-slate-300"
                      >
                        {docsSavingFlags ? "Salvo…" : "💾 Salva modifiche flag"}
                      </Button>
                    )}
                    {docsPendingNotifyIds.length > 0 && (
                      <Button
                        size="sm"
                        data-testid="docs-open-notify-btn"
                        onClick={() => setDocsNotifyOpen(true)}
                        className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        📧 Invia notifiche ({docsPendingNotifyIds.length})
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {docs.map((a) => {
                const flags = docsPendingFlags[a.id] || {};
                const isVisible = flags.client_visible ?? !!a.client_visible;
                const isNotify = flags.notify_email_pending ?? !!a.notify_email_pending;
                const setFlag = (key, val) => setDocsPendingFlags((s) => ({ ...s, [a.id]: { ...(s[a.id] || { client_visible: !!a.client_visible, notify_email_pending: !!a.notify_email_pending }), [key]: val } }));
                return (
                <div key={a.id} className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    data-testid={`send-doc-check-${a.id}`}
                    checked={!!docsSelectedEmail[a.id]}
                    onChange={(e) => setDocsSelectedEmail({ ...docsSelectedEmail, [a.id]: e.target.checked })}
                    className="mt-2 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <div className="flex-1">
                    <AttachmentItem att={a} onDownload={downloadDoc} onDelete={removeDoc} onPreview={() => setLightboxIndex(docs.findIndex((d) => d.id === a.id))} onRequestSignature={requestSignatureForDoc} />
                    <div className="mt-1 flex items-center gap-4 text-xs pl-1">
                      <label className="inline-flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`doc-flag-visible-${a.id}`}
                          checked={isVisible}
                          onChange={(e) => setFlag("client_visible", e.target.checked)}
                          className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 cursor-pointer"
                        />
                        <span className={isVisible ? "text-emerald-700 font-medium" : "text-slate-500"}>👁️ Visibile cliente</span>
                      </label>
                      <label className="inline-flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`doc-flag-notify-${a.id}`}
                          checked={isNotify}
                          onChange={(e) => setFlag("notify_email_pending", e.target.checked)}
                          className="h-3.5 w-3.5 rounded border-slate-300 text-amber-600 cursor-pointer"
                        />
                        <span className={isNotify ? "text-amber-700 font-medium" : "text-slate-500"}>📧 Notifica email</span>
                      </label>
                      {a.last_notified_at && (
                        <span className="text-[10px] text-slate-400 italic">Notificato: {formatDateTime(a.last_notified_at)}</span>
                      )}
                    </div>
                  </div>
                </div>
              );})}
            </div>
            {docsSendLogs.length > 0 && (
              <div className="mt-4 bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="docs-send-logs">
                <Label className="text-xs uppercase tracking-wide text-slate-600 flex items-center gap-1.5 mb-2">
                  <Mail className="h-3.5 w-3.5" /> Storico invii ({docsSendLogs.length})
                </Label>
                <div className="space-y-1.5 max-h-44 overflow-y-auto">
                  {docsSendLogs.map((l) => (
                    <div key={l.id} className="text-xs flex items-start gap-2 border-l-2 border-emerald-400 pl-2 py-0.5">
                      <span className={`text-[10px] uppercase font-bold rounded px-1 ${l.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {l.status === "sent" ? "✓" : "✗"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="truncate"><span className="font-semibold">{l.recipient}</span> · {formatDateTime(l.sent_at)}</div>
                        <div className="text-slate-500 truncate">{(l.attachment_names || []).join(", ")}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 🆕 17/07/2026 — Dialog notifica batch documenti cliente */}
      <Dialog open={docsNotifyOpen} onOpenChange={(o) => { setDocsNotifyOpen(o); if (!o) setDocsNotifyMsg(""); }}>
        <DialogContent className="max-w-lg" data-testid="docs-notify-batch-dialog">
          <DialogHeader>
            <DialogTitle>📧 Invia notifica cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              Sto per inviare UNA email di riepilogo al cliente <strong>{docsOpen?.surname} {docsOpen?.name}</strong>.
            </p>
            {(() => {
              const files = (docs || []).filter((a) => docsPendingNotifyIds.includes(a.id));
              return (
                <div className="p-2.5 rounded bg-blue-50 border border-blue-200 text-xs max-h-40 overflow-y-auto">
                  <p className="font-semibold text-blue-900 mb-1">File inclusi ({files.length}):</p>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {files.map((f) => {
                      const vis = f.client_visible || docsPendingFlags[f.id]?.client_visible;
                      return (
                        <li key={f.id} className={vis ? "text-slate-700" : "text-red-600"}>
                          {f.original_filename}
                          {!vis && <span className="text-[10px] ml-1">(⚠ non visibile)</span>}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })()}
            <div>
              <Label className="text-xs">Messaggio personalizzato (opzionale)</Label>
              <textarea
                data-testid="docs-notify-msg"
                value={docsNotifyMsg}
                onChange={(e) => setDocsNotifyMsg(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="Es. Ti allego i documenti richiesti..."
                className="w-full mt-1 p-2 border border-slate-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-[10px] text-slate-500 mt-0.5">{docsNotifyMsg.length}/1000</p>
            </div>
            {docsFlagsDirty && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                ⚠ Ci sono flag non salvati. Verranno salvati automaticamente prima dell&apos;invio.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocsNotifyOpen(false)} disabled={docsNotifyBusy} data-testid="docs-notify-cancel">Annulla</Button>
            <Button onClick={sendDocsNotification} disabled={docsNotifyBusy || docsPendingNotifyIds.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="docs-notify-confirm">
              {docsNotifyBusy ? "Invio…" : `Salva e Notifica (${docsPendingNotifyIds.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog invio documenti cliente via email (allegati zippati) */}
      <Dialog open={docsSendOpen} onOpenChange={(o) => { if (!o && !docsSendBusy) setDocsSendOpen(false); }}>
        <DialogContent className="max-w-lg" data-testid="send-docs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Mail className="h-5 w-5" /> Invia documenti via email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded p-2.5 text-xs">
              <strong>{Object.values(docsSelectedEmail).filter(Boolean).length}</strong> file selezionati:
              <ul className="mt-1 list-disc list-inside text-slate-700 max-h-24 overflow-y-auto">
                {docs.filter((a) => docsSelectedEmail[a.id]).map((a) => (
                  <li key={a.id} className="truncate">{a.original_filename}</li>
                ))}
              </ul>
            </div>
            <div>
              <Label className="text-xs">Destinatario *</Label>
              <Input type="email" value={docsSendForm.to_email} onChange={(e) => setDocsSendForm({ ...docsSendForm, to_email: e.target.value })} placeholder="destinatario@example.it" data-testid="docs-send-email" />
            </div>
            <div>
              <Label className="text-xs">Oggetto</Label>
              <Input value={docsSendForm.subject} onChange={(e) => setDocsSendForm({ ...docsSendForm, subject: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Messaggio</Label>
              <Textarea rows={5} value={docsSendForm.body} onChange={(e) => setDocsSendForm({ ...docsSendForm, body: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocsSendOpen(false)} disabled={docsSendBusy}>Annulla</Button>
            <Button
              data-testid="confirm-send-docs"
              onClick={confirmDocsSend}
              disabled={docsSendBusy || !docsSendForm.to_email}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {docsSendBusy ? "Invio…" : <><Mail className="h-4 w-4 mr-1.5" /> Invia</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione documento cliente */}
      <Dialog open={!!docDeleteTarget} onOpenChange={(o) => { if (!o && !docDeleting) setDocDeleteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="confirm-delete-doc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Eliminare il documento?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p>Stai per eliminare il file:</p>
            <p className="font-semibold bg-slate-50 border border-slate-200 rounded px-3 py-2 break-all">
              {docDeleteTarget?.label}
            </p>
            <p className="text-xs text-red-600">L&apos;operazione non può essere annullata.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="cancel-delete-doc" onClick={() => setDocDeleteTarget(null)} disabled={docDeleting}>
              Annulla
            </Button>
            <Button
              data-testid="confirm-delete-doc"
              onClick={confirmRemoveDoc}
              disabled={docDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {docDeleting ? "Eliminazione…" : "Elimina"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione singolo cliente (con OTP) */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !deleting) { setDeleteTarget(null); setDeleteOtpCode(""); } }}>
        <DialogContent className="max-w-md" data-testid="confirm-delete-client-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Eliminare il cliente?
            </DialogTitle>
          </DialogHeader>
          {deleteOtpStage === "confirm" && (
            <div className="text-sm text-slate-700 space-y-2">
              <p>Stai per eliminare definitivamente:</p>
              <p className="font-semibold bg-slate-50 border border-slate-200 rounded px-3 py-2">
                {deleteTarget?.label}
              </p>
              <p className="text-xs text-red-600">
                Saranno cancellate anche TUTTE le polizze, i documenti e l&apos;eventuale accesso al portale collegato.
                L&apos;operazione richiede un codice OTP via email per essere confermata.
              </p>
            </div>
          )}
          {deleteOtpStage === "otp" && (
            <div className="text-sm text-slate-700 space-y-3">
              <p>
                Codice OTP inviato a <strong>{deleteOtpEmail}</strong>. Inserisci il codice per confermare l&apos;eliminazione di:
              </p>
              <p className="font-semibold bg-slate-50 border border-slate-200 rounded px-3 py-2">
                {deleteTarget?.label}
                {deleteOtpInfo?.policies_count > 0 && (
                  <span className="block text-xs text-red-700 mt-1">+ {deleteOtpInfo.policies_count} polizze associate</span>
                )}
              </p>
              <Input
                data-testid="client-delete-otp-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="6 cifre"
                value={deleteOtpCode}
                onChange={(e) => setDeleteOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="text-center text-lg font-mono tracking-[0.5em]"
              />
              <button type="button" onClick={requestDeleteOtp} disabled={deleting} className="text-xs text-blue-600 hover:underline" data-testid="client-delete-otp-resend">
                Rigenera codice
              </button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="cancel-delete-client" onClick={() => { setDeleteTarget(null); setDeleteOtpCode(""); }} disabled={deleting}>
              Annulla
            </Button>
            {deleteOtpStage === "confirm" ? (
              <Button
                data-testid="request-delete-client-otp"
                onClick={requestDeleteOtp}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? "Invio OTP…" : "Invia codice OTP"}
              </Button>
            ) : (
              <Button
                data-testid="confirm-delete-client"
                onClick={confirmDelete}
                disabled={deleting || deleteOtpCode.length < 4}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? "Eliminazione…" : "Elimina definitivamente"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma bulk delete clienti */}
      <Dialog open={bulkDeleteOpen} onOpenChange={(o) => { if (!o && !bulkDeleting) setBulkDeleteOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="confirm-bulk-delete-clients-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Eliminare {selectedIds.size} client{selectedIds.size === 1 ? "e" : "i"}?
            </DialogTitle>
          </DialogHeader>
          {bulkOtpStage === "confirm" && (
            <div className="text-sm text-slate-700 space-y-2">
              <p>Stai per eliminare definitivamente:</p>
              <ul className="text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2 max-h-32 overflow-y-auto list-disc pl-6">
                {items.filter((c) => selectedIds.has(c.id)).slice(0, 10).map((c) => (
                  <li key={c.id}>{c.surname} {c.name}</li>
                ))}
                {selectedIds.size > 10 && <li className="italic text-slate-500">… e altri {selectedIds.size - 10}</li>}
              </ul>
              <p className="text-xs text-red-600 font-semibold">
                ⚠️ Saranno cancellate TUTTE le polizze, i documenti e gli accessi al portale collegati.
                L&apos;operazione richiede un codice OTP via email per essere confermata.
              </p>
            </div>
          )}
          {bulkOtpStage === "otp" && (
            <div className="text-sm text-slate-700 space-y-3">
              <p>
                Codice OTP inviato a <strong>{bulkOtpEmail}</strong>. Inserisci il codice per confermare l&apos;eliminazione di
                <strong> {bulkOtpInfo?.clients_count || selectedIds.size}</strong> clienti
                {bulkOtpInfo?.policies_count > 0 && <span> e <strong>{bulkOtpInfo.policies_count}</strong> polizze associate</span>}.
              </p>
              <Input
                data-testid="clients-bulk-delete-otp-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="6 cifre"
                value={bulkOtpCode}
                onChange={(e) => setBulkOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="text-center text-lg font-mono tracking-[0.5em]"
              />
              <button type="button" onClick={requestBulkDeleteOtp} disabled={bulkDeleting} className="text-xs text-blue-600 hover:underline" data-testid="clients-bulk-delete-otp-resend">
                Rigenera codice
              </button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="cancel-bulk-delete-clients" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>
              Annulla
            </Button>
            {bulkOtpStage === "confirm" ? (
              <Button
                data-testid="request-bulk-delete-clients-otp"
                onClick={requestBulkDeleteOtp}
                disabled={bulkDeleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {bulkDeleting ? "Invio OTP…" : "Invia codice OTP"}
              </Button>
            ) : (
              <Button
                data-testid="confirm-bulk-delete-clients"
                onClick={confirmBulkDelete}
                disabled={bulkDeleting || bulkOtpCode.length < 4}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {bulkDeleting ? "Eliminazione…" : `Elimina ${selectedIds.size} definitivamente`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ClientTimelineDialog
        client={timelineClient}
        open={!!timelineClient}
        onClose={() => setTimelineClient(null)}
      />

      {/* Sub-dialog anteprima/modifica email credenziali */}
      <Dialog open={!!credsEmailDialog} onOpenChange={(o) => { if (!o && !credsEmailBusy) setCredsEmailDialog(null); }}>
        <DialogContent className="max-w-lg" data-testid="creds-email-preview">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Mail className="h-5 w-5" /> Invia credenziali via email
            </DialogTitle>
          </DialogHeader>
          {credsEmailDialog && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-800">
                ℹ L&apos;intestazione completa dell&apos;agenzia (logo + P.IVA + RUI) viene aggiunta automaticamente in cima all&apos;email.
              </div>
              <div>
                <Label className="text-xs">Destinatario *</Label>
                <Input type="email" value={credsEmailDialog.to_email} onChange={(e) => setCredsEmailDialog({ ...credsEmailDialog, to_email: e.target.value })} data-testid="creds-to-email" />
              </div>
              <div>
                <Label className="text-xs">Oggetto</Label>
                <Input value={credsEmailDialog.subject} onChange={(e) => setCredsEmailDialog({ ...credsEmailDialog, subject: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Messaggio</Label>
                <Textarea rows={9} value={credsEmailDialog.body} onChange={(e) => setCredsEmailDialog({ ...credsEmailDialog, body: e.target.value })} data-testid="creds-body" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCredsEmailDialog(null)} disabled={credsEmailBusy}>Annulla</Button>
            <Button data-testid="confirm-creds-send" onClick={confirmSendCredsEmail} disabled={credsEmailBusy || !credsEmailDialog?.to_email} className="bg-blue-600 hover:bg-blue-700 text-white">
              {credsEmailBusy ? "Invio…" : <><Mail className="h-4 w-4 mr-1.5" /> Invia ora</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-dialog anteprima messaggio WhatsApp credenziali */}
      <Dialog open={!!credsWaDialog} onOpenChange={(o) => { if (!o) setCredsWaDialog(null); }}>
        <DialogContent className="max-w-lg" data-testid="creds-wa-preview">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              💬 Anteprima messaggio WhatsApp
            </DialogTitle>
          </DialogHeader>
          {credsWaDialog && (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded p-2 text-[11px] text-green-800">
                Destinatario: <span className="font-mono font-semibold">{credsWaDialog.displayPhone}</span> · Personalizza il testo prima di aprire WhatsApp.
              </div>
              <div>
                <Label className="text-xs">Messaggio</Label>
                <Textarea rows={10} value={credsWaDialog.message} onChange={(e) => setCredsWaDialog({ ...credsWaDialog, message: e.target.value })} data-testid="creds-wa-message" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCredsWaDialog(null)}>Annulla</Button>
            <Button data-testid="confirm-creds-wa" onClick={confirmWhatsAppSend} className="bg-green-600 hover:bg-green-700 text-white">
              💬 Apri WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Lightbox anteprima allegati a tutto schermo */}
      {lightboxIndex !== null && docs.length > 0 && lightboxIndex >= 0 && lightboxIndex < docs.length && (
        <AttachmentLightbox
          items={docs}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
          onDownload={downloadDoc}
        />
      )}
      {/* 🆕 Dialog invio messaggio cliente */}
      <SendMessageDialog
        open={!!msgTarget}
        onClose={() => setMsgTarget(null)}
        clientId={msgTarget?.id}
        clientLabel={msgTarget ? `${msgTarget.surname || ""} ${msgTarget.name || ""}`.trim() : ""}
        templateListKey="client_message_templates"
        title="Invia messaggio al cliente"
        subtitle="Invia una comunicazione via Email e/o WhatsApp scegliendo tra i template dell'agenzia."
      />
      {/* 🆕 Dialog anteprima email prima dell'invio (credenziali portale) */}
      <EmailPreviewDialog
        open={!!emailPreviewCtx}
        onOpenChange={(o) => { if (!o) setEmailPreviewCtx(null); }}
        emailTypeKey={emailPreviewCtx?.typeKey}
        title={emailPreviewCtx?.title || "Anteprima email prima dell'invio"}
        fetchPreview={emailPreviewCtx?.fetchPreview || (async () => ({ subject: "", html_body: "", to: [] }))}
        onConfirmSend={async () => {
          if (emailPreviewCtx?.onConfirm) await emailPreviewCtx.onConfirm();
          setEmailPreviewCtx(null);
        }}
      />
    </div>
  );
}
